/**
 * ClipVideoCreatorAdapter - Lightweight adapter implementing IVideoCreatorStore interface
 * for clip preview context in Content Studio.
 *
 * This adapter bridges the clip preview renderer infrastructure with the VideoTemplatesProvider,
 * allowing template operations without the full VideoCreatorStore.
 *
 * KEY DESIGN: The adapter delegates to the real VideoCreatorStore for services (textBrandingService,
 * karaokeProducer, etc.) but uses clip-specific stores for editor operations.
 */

import * as EventTracker from '@src/analytics/EventTracker';
import { TextBrandingService } from '@src/components/textProcessor/TextBrandingService';
import { type Command, EditorChangeType, Element } from '@src/editor';
import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { produceFirstCaptions } from '@src/hooks/produceFirstCaptions';
import {
  type FaceDetection,
  fetchFaceDetectionsApi,
} from '@src/hooks/useFaceDetections';
import {
  type ElementState,
  mapToElementState,
} from '@src/renderer/ElementState';
import {
  type BackgroundSaveResult,
  backgroundVideoSaveService,
} from '@src/services/BackgroundVideoSaveService';
import { err } from '@src/services/common/Result';
import { syncVideoToStoryEntries } from '@src/services/common/syncVideoToStory';
import {
  fetchParsedScenesApi,
  type SceneData,
} from '@src/services/framing/SceneDetectionService';
import {
  areCaptionsHidden,
  setCaptionsHidden,
} from '@src/services/karaoke/captionsVisibility';
import { KaraokeTemplateVariantService } from '@src/services/karaoke/KaraokeTemplateVariantService';
import {
  buildLocalCanvasUrl,
  isLocalCanvasSource,
  requiresNativeLocalPreview,
} from '@src/services/LocalCanvasPreview';
import { addSectionsToReel } from '@src/services/reel/addSectionsToReel';
import { applyChapterization } from '@src/services/reel/applyChapterization';
import { deriveReelSections } from '@src/services/reel/deriveReelSections';
import { deriveSubClipsFromTimeline } from '@src/services/reel/deriveSubClipsFromTimeline';
import { pruneReelSectionNames } from '@src/services/reel/pruneReelSectionNames';
import {
  appendChapterToReel,
  chapterAnchorsChanged,
  insertChapterInReel,
  moveEmptyChapterInReel,
  moveSectionInReelChapters,
  removeAllChaptersFromReel,
  removeChapterFromReel,
  renameChapterInReel,
  restoreChapterAnchors,
  setChapterCollapsed,
} from '@src/services/reel/reelChapters';
import { EMPTY_REEL } from '@src/services/reel/reelTypes';
import { renameSectionInReel } from '@src/services/reel/renameSectionInReel';
import { replaceSectionInReel } from '@src/services/reel/replaceSectionInReel';
import {
  disableSectionInReel,
  enableSectionInReel,
  reanchorDisabledSections,
  removeDisabledSectionFromReel,
} from '@src/services/reel/toggleReelSection';
import { VideoVersionHistoryService } from '@src/services/videoVersions/VideoVersionHistoryService';
import { AdaptivePlaybackSourceManager } from '@src/stores/lib/AdaptivePlaybackSourceManager';
import { autoFrameNewAspectRatio } from '@src/stores/lib/AutoFramingHelper';
import detectAndApplyManualChangesToKaraokeConfig from '@src/stores/lib/onStateChangeHandlers/detectAndApplyManualChangesToKaraokeConfig';
import detectAndApplyManualChangesToTextConfig from '@src/stores/lib/onStateChangeHandlers/detectAndApplyManualChangesToTextConfig';
import { createScenesFromDetection } from '@src/stores/lib/SceneCreationHelper';
import SceneLayoutSelectionManager from '@src/stores/lib/SceneLayoutSelectionManager';
import StateChangeObserver from '@src/stores/lib/StateChangeObserver';
import { ClipVersionPreviewStore } from '@src/stores-v2/ClipVersionPreviewStore';
import {
  captureKaraokeLanguageState,
  resolveRestoredUiLanguage,
  seedKaraokeLanguageFromSnapshot,
} from '@src/stores-v2/karaokeLanguageSnapshot';
import {
  AspectRatio as AspectRatioEnum,
  VideoDimensionsHeight,
  VideoDimensionsWidth,
  VideoResolution,
} from '@src/types.ts/video';
import {
  classifyCompositionMedia,
  isAudioFirstSource,
  isPendingEraElement,
  remapElementMetaIds,
  remapSceneAnchorIds,
  repointMediaElements,
} from '@src/utility/audioFirstSwap';
import { deepCloneSafe } from '@src/utility/deepCloneSafe';
import { getElementMetaObject } from '@src/utility/extraElementData';
import { getStandardDimensionsForAspectRatio } from '@src/utility/getStandardDimensionsForAspectRatio';
import { LogManager } from '@src/utility/Logger';
import { extractTranscriptionText } from '@src/utility/transcriptFormatUtils';
import { DEFAULT_SCENE_MANIPULATION_LIMIT } from '@src/utility/useFlagsCombination';
import { debounce } from 'lodash';
import { makeAutoObservable, reaction, runInAction, toJS } from 'mobx';
import { v4 as uuid } from 'uuid';

import { extractErrorInfo } from '../../../utility/error';
import { hydrateOriginalMediaDuration } from '../../../utility/timeline';
import { createClipRootProxy } from './clipTranscriptProxies';

import type { TextProducer } from '@src/components/textProcessor/TextProducer';
import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { KaraokeData } from '@src/editor/types/karaoke';
import type { Renderer } from '@src/renderer/Renderer';
import type { RendererState } from '@src/renderer/RendererState';
import type CustomFontsService from '@src/services/CustomFontsService';
import type KaraokeProducer from '@src/services/karaoke/KaraokeProducer';
import type { AddedReelSection } from '@src/services/reel/addSectionsToReel';
import type { ChapterBoundary } from '@src/services/reel/chapterizationResponse';
import type { ReelSectionMove } from '@src/services/reel/reelChapters';
import type { NameSource, Reel } from '@src/services/reel/reelTypes';
import type { ReelSectionReplacement } from '@src/services/reel/replaceSectionInReel';
import type { DisableReelSectionInput } from '@src/services/reel/toggleReelSection';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';
import type {
  CurrentLoadedVideo,
  VideoStateSnapshot,
} from '@src/stores/VideoCreatorStore';
import type {
  ClipEditSessionStore,
  VideoClipPreview,
} from '@src/stores-v2/ClipEditSessionStore';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type { RootStore } from '@src/stores-v2/RootStore';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import type { DesignCard, SidebarOption } from '@src/types.ts/general';
import type {
  AssociatedVideo,
  ClipMetadata,
  DatoStory,
  ExtraElementData,
  ShareableImageType,
  Showcase,
  SourcePlatform,
  Story,
  StoryDTO,
  Video,
  VideoAction,
} from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type { AspectRatio, VideoSource } from '@src/types.ts/video';
import type { CompositionMediaKind } from '@src/utility/audioFirstSwap';

/**
 * Lightweight utility to extract karaoke data directly from an editor instance.
 * Avoids instantiating a full KaraokeStore just to read data. The saved shape
 * (PLAT-11905): the source-timed rows on top whichever caption set is shown.
 */
function getKaraokeDataFromEditor(editor: IVideoEditor): KaraokeData | null {
  return editor.getCaptionData();
}

const logger = LogManager.getLogger('adapter:ClipVideoCreatorAdapter');

const MAX_UNDO_REDO_STACK_SIZE = 10;
const AUTO_SAVE_DEBOUNCE_MS = 1000;
/** Quiet time after a refused canvas edit before the canvas is put back. */
const CANVAS_RESTORE_DELAY_MS = 300;

/**
 * Longest a publish waits for a dub run that is still out before giving up on
 * it.
 *
 * A bound is needed at all because the slow part of a dub run is a network read
 * and `authenticatedFetch` carries no timeout of its own — a stalled connection
 * leaves that promise pending for good, and a publish pressed just after the
 * Voice control would then never happen at all.
 *
 * Long rather than short, deliberately: expiring early costs a publish that
 * ships the original language with the new language's captions burned over it,
 * while expiring late only delays a publish whose progress the person can
 * already see.
 */
export const DUB_RUN_WAIT_TIMEOUT_MS = 15_000;

/**
 * How a wait for a dub run ended.
 *
 * - `no-run` — nothing was out; this is every publish with no dub involved.
 * - `settled` — the run finished, so the clip carries whatever it produced.
 * - `expired` — the bound passed, the run was given up on, and the clip still
 *   holds the ORIGINAL speech. A publish made on this must not go out.
 */
export type PendingDubRunWaitOutcome = 'no-run' | 'settled' | 'expired';

/**
 * The seam a publish uses to wait for a dub run that a person started moments
 * earlier.
 *
 * The Voice control and the export panel are two separate subtrees of the clip
 * preview modal, and the control sits BELOW the panel, so a run being out
 * cannot be handed upward without threading a prop through the tree — and a
 * boolean would only say "busy" anyway, leaving the panel to poll for the end
 * of it. This adapter is the one object both subtrees already hold, and it
 * already carries the same shape for the other pending job on this screen.
 */
export interface PendingDubRunHost {
  /**
   * Register a dub run so a publish can wait for it.
   *
   * `cancel` is how the run is given up when the wait's bound expires, and it is
   * required: a run that cannot be given up leaves a stretch in which the screen
   * says a dub is on its way, no publish waits for anything, and a publish made
   * meanwhile delivers the original language.
   */
  trackDubRun(run: Promise<unknown>, cancel: () => void): void;
  /**
   * Wait, but never for good, for the dub run that is out. Says HOW the wait
   * ended, because the caller has to act differently on `expired`.
   */
  waitForPendingDubRun(): Promise<PendingDubRunWaitOutcome>;
}

/**
 * Minimal VideoLike interface for extraElementData storage
 */
export interface ClipVideoLike {
  extraElementData: Record<string, unknown>;
  id?: string;
  title?: string;
}

/**
 * Options for creating the ClipVideoCreatorAdapter
 */
export interface ClipVideoCreatorAdapterOptions {
  editorStore: ClipPreviewEditorStore;
  videoSourceStore: VideoSourceStore;
  renderer: Renderer | null;
  story?: Story;
  organization?: Showcase;
  /** The real VideoCreatorStore for delegating services */
  realVideoCreator: IVideoCreatorStore;
  /** Session store that owns all local clip variant state */
  sessionStore: ClipEditSessionStore;
  flags?: {
    enableAutoFraming?: boolean;
    sceneManipulationLimit?: number;
    // Static editor-store configuration flags (creatorStudioLayoutStrategy,
    // lockSceneBoundElements, creatorStudioTimelineWindowingConfig) are
    // applied directly to the editor store by `useClipPreviewRenderer`
    // before this adapter is constructed — no need to forward them here.
  };
}

/**
 * ClipVideoCreatorAdapter implements a subset of IVideoCreatorStore
 * to enable VideoTemplatesProvider to work with clip preview context.
 *
 * It delegates services to the real VideoCreatorStore while using
 * clip-specific stores for editor operations.
 */
export class ClipVideoCreatorAdapter
  implements Partial<IVideoCreatorStore>, PendingDubRunHost
{
  // Core stores
  private _editorStore: ClipPreviewEditorStore;
  private _videoSourceStore: VideoSourceStore;
  private _renderer: Renderer | null;

  // The real VideoCreatorStore for service delegation
  private _realVideoCreator: IVideoCreatorStore;

  // Session store — single owner of all local clip variant data.
  // Replaces the old `_currentVideo` field; all reads/writes go through here.
  private _sessionStore: ClipEditSessionStore;

  // Cached proxied RootStore so that videoCreator.rootStore.editorStore
  // returns the clip's editor (not the global one). Critical for transcript
  // operations in useTranscriptOperations that access editor via this path.
  private _proxiedRootStore: RootStore | null = null;

  // Services
  private _textBrandingService: TextBrandingService;
  private _extraElementDataDisposer: (() => void) | undefined;
  private _adaptivePlaybackSourceManager?: AdaptivePlaybackSourceManager;
  private _sceneLayoutSelectionManager!: SceneLayoutSelectionManager;

  private _stateChangeObserver: StateChangeObserver = new StateChangeObserver([
    detectAndApplyManualChangesToKaraokeConfig,
    detectAndApplyManualChangesToTextConfig,
  ]);
  private _stateChangeUnsubscribe: (() => void) | null = null;
  private _prevRendererState: RendererState | undefined = undefined;

  // Cached via focus listeners so onStateChange reads a stable value rather
  // than probing document.activeElement mid-event (can flicker during drags).
  isIframeActive: boolean = false;
  private _iframeFocusListener?: () => void;

  // Local state for clip editing
  private _time: number = 0;
  private _isInitialized: boolean = true;
  showSubtitles: boolean = false;

  // Undo/redo snapshots for clip preview
  private undoSnapshots: Array<VideoStateSnapshot> = [];
  private redoSnapshots: Array<VideoStateSnapshot> = [];

  /**
   * Clip History: a saved version shown read-only. While one is, edits are
   * ignored and nothing saves.
   */
  readonly versionPreview: ClipVersionPreviewStore;
  private _versionHistoryService: VideoVersionHistoryService;
  /** Puts the canvas back after a refused edit (`refuseEditWhilePreviewing`). */
  private _restoreCanvas = debounce(
    () => this._editorStore.refreshTimeline(),
    CANVAS_RESTORE_DELAY_MS,
  );

  // Pending save queue — prevents double-saves when debounced auto-save and
  // template-triggered saves overlap (mirrors VideoCreatorStore pattern).
  private _pendingNextSave: {
    withRenderer: boolean;
    autoSave: boolean;
  } | null = null;

  // Resolvers for callers awaiting the current background save cycle
  // (mirrors VideoCreatorStore._backgroundSaveResolvers).
  private _backgroundSaveResolvers: Array<{
    resolve: () => void;
    reject: (err: Error) => void;
  }> = [];

  private _pendingDubRun: Promise<void> | null = null;

  // Always set and cleared together with the run above, so the pair can never
  // name two different runs.
  private _cancelPendingDubRun: (() => void) | null = null;

  // Debounced auto-save timer
  private _debouncedAutoSave = debounce((skipLastActionUpdate = false) => {
    this.saveStoryAndVideo(false, true, true, true, skipLastActionUpdate);
  }, AUTO_SAVE_DEBOUNCE_MS);

  /**
   * PLAT-11063 — what the next save will be flushing. Filled where a save is
   * scheduled (the patched `executeCommand` and `_updateReel`, the only two),
   * drained into `reel_editor_saved` when one completes.
   *
   * A set rather than a single cause, because saves coalesce twice over: the
   * debounce collapses a burst of edits, and a save can queue behind one already
   * in flight. Naming one cause would be a guess about which of them mattered.
   */
  private _pendingSaveReasons = new Set<string>();

  /** True when the save about to run was the one queued behind another. */
  private _nextSaveQueued = false;

  // Save state
  // Initialised, not bare: with `useDefineForClassFields: false` a field
  // without one emits no assignment, so `makeAutoObservable` never sees it.
  savingError: (Error & { humanMessage?: string }) | undefined = undefined;
  /**
   * When a background save last PERSISTED something. Set only where a result
   * comes back (`_applyBackgroundSaveResult`) — `isBackgroundSaving` going
   * false only says a run ended, which is also true of the runs that skip
   * (already-in-flight, missing deps) and persist nothing. The save-status
   * cloud reads this rather than inferring a save from the absence of an
   * error (PLAT-11654).
   */
  lastSavedAt: Date | null = null;
  photoDataForDato: Record<
    string,
    {
      type: 'quotes' | 'stock' | 'ai' | 'artifact';
      url: string;
    }
  > = {};
  currentVideoLoadingWorkflowId: string | null = null;
  currentVideoVersions?: any[] = [];

  // Pending template re-application after aspect ratio change
  pendingTemplateReapplyId: string | null = null;

  /** Filled in by VideoTemplatesProvider. Calls handleAddVideoTemplate(template, true, true)
   *  to re-apply a template with the correct AR-specific config, without triggering a save. */
  reapplyTemplate?: (templateId: string) => Promise<void>;

  // UI state flags (minimal implementation)
  autoSwitchToKaraokeSidebar: boolean = false;
  designSubMenu: DesignCard = 'none' as DesignCard;
  sidebarOptions: SidebarOption = 'design' as SidebarOption;
  openPhotoElementReplacementModal: IVideoCreatorStore['openPhotoElementReplacementModal'] =
    null;

  // Minimal TimelineStore implementation
  // Use any to satisfy IVideoCreatorStore interface while providing minimal implementation
  timelineStore: any = {
    framerate: 30,
  };

  private _flags?: {
    enableAutoFraming?: boolean;
    sceneManipulationLimit?: number;
    autoFramingStrategy?: string;
  };

  constructor(options: ClipVideoCreatorAdapterOptions) {
    this._editorStore = options.editorStore;
    this._videoSourceStore = options.videoSourceStore;
    this._renderer = options.renderer;
    this._realVideoCreator = options.realVideoCreator;
    this._sessionStore = options.sessionStore;
    this._flags = options.flags;

    // Static editor-store configuration (preview layout config + windowing
    // config) is applied by `useClipPreviewRenderer` BEFORE this constructor
    // runs, so it's already in effect by the time any reader hits the editor
    // store. The adapter only wires runtime callbacks below — those need
    // closures over adapter-instance state and can't move.

    // Wire current-time closure so the editor store can resolve the correct
    // scene at the current playhead position (for RendererSyncService and
    // hybrid-adaptive layout).
    this._editorStore.setPlaybackCallbacks({
      getCurrentTime: () => this._time,
      getIsPlaying: () => this.isPlaying,
      getIsScrubbing: () => this.isScrubbing,
    });

    this._textBrandingService = new TextBrandingService(
      this as unknown as VideoCreatorStore,
      this._realVideoCreator.datoClientStore,
      this._editorStore.editor,
    );

    this._versionHistoryService = new VideoVersionHistoryService({
      videoRepository: this._realVideoCreator.datoClientStore.videoRepository,
    });
    this.versionPreview = this.createVersionPreview();

    this._sceneLayoutSelectionManager = new SceneLayoutSelectionManager(
      () => this._editorStore.sceneLayoutIndexStore,
      id => {
        const elementSource = this._videoSourceStore.findElementById(id);
        if (!elementSource) {
          // See VideoCreatorStore's resolver: windowing can prune a
          // representation the index still advertises, and throwing would abort
          // the caller's MobX reaction every frame (PLAT-11065). Clip preview is
          // always windowed, so this is the more exposed of the two surfaces.
          logger.warn(
            'Layout representation not found in video source; keeping the unresolved element',
            { elementId: id, time: this._time },
          );
          return undefined;
        }
        return mapToElementState(elementSource);
      },
      () => this.activeElementIds,
      () => this._time,
    );

    this.timelineStore.framerate =
      parseFloat(this._sessionStore.activeVariant.videoSource?.frame_rate) ||
      24;

    makeAutoObservable(this, {
      _editorStore: false,
      _videoSourceStore: false,
      _renderer: false,
      _realVideoCreator: false,
      _sessionStore: false,
      versionPreview: false,
      _versionHistoryService: false,
      _restoreCanvas: false,
      _debouncedAutoSave: false,
      _pendingNextSave: false,
      _backgroundSaveResolvers: false,
      _pendingDubRun: false,
      _cancelPendingDubRun: false,
      _adaptivePlaybackSourceManager: false,
      _stateChangeObserver: false,
      _stateChangeUnsubscribe: false,
      _prevRendererState: false,
      _iframeFocusListener: false,
      _sceneLayoutSelectionManager: false,
      // Getters that read from refs marked false above must also be false,
      // otherwise MobX caches the computed and never invalidates it when the
      // underlying ref changes
      currentVideo: false,
      duration: false,
      state: false,
      // Non-reactive flag — toggled by the review workspace, read in seek
      // callbacks (PLAT-10286).
      suppressRendererPlayback: false,
    });

    // Enable hybrid-adaptive refresh for clip previews — delegates two MobX
    // reactions (time + isPlaying) to the shared manager.
    // IMPORTANT: must be after makeAutoObservable so _time and isPlaying are
    // observable — otherwise the reaction data functions read plain properties
    // and MobX never tracks/fires them.
    this._adaptivePlaybackSourceManager = new AdaptivePlaybackSourceManager({
      getTime: () => this._time,
      getIsPlaying: () => this.isPlaying,
      getIsScrubbing: () => this.isScrubbing,
      getNeedsRefresh: (time: number) =>
        this._editorStore.needsHybridAdaptiveRefresh(time) ||
        this._editorStore.needsWindowingRefresh(time),
      refreshTimeline: () => this._editorStore.refreshTimeline(),
    });

    // Mirror VideoCreatorStore: capture a clip snapshot before each command
    // so undo/redo operates on our own snapshot stack instead of the editor's
    // internal history.
    try {
      const editor = this._editorStore?.editor;
      if (editor) {
        const originalExecute = editor.executeCommand.bind(editor);

        editor.executeCommand = (command: Command) => {
          // Clip History preview is read-only: an edit here would land on the
          // old version and be lost on leaving it.
          if (this.versionPreview.isPreviewing) {
            this.refuseEditWhilePreviewing(command);
            return false;
          }
          const shouldSnapshot = command?.shouldCreateUndoRedoEntry?.();
          if (shouldSnapshot) {
            try {
              const label = command?.getDescription?.();
              this.saveVideoStateSnapshot('undo', label);
              this.redoSnapshots = [];
            } catch (err) {
              logger.error('Clip undo/redo snapshot failed', {
                error: extractErrorInfo(err),
              });
            }
          }

          const result = originalExecute(command);

          if (shouldSnapshot && result) {
            this._pendingSaveReasons.add(
              command?.getName?.() ?? 'unknown_command',
            );
            this._debouncedAutoSave();
          }

          return result;
        };
      }
    } catch (err) {
      logger.warn('Failed to patch clip executeCommand for snapshots', {
        error: err instanceof Error ? err.message : String(err),
      });
    }

    // Reactive sync: mirror extraElementData changes to the editor,
    // matching VideoCreatorStore.onExtraElementDataChange (lines 798-800).
    // Observes the session store's activeVariant (single data owner).
    this._extraElementDataDisposer = reaction(
      () => this._sessionStore.activeVariant.extraElementData,
      eed => {
        this._editorStore.editor.passExtraElementData(eed ?? {});
      },
    );

    this.setupIframeActiveElementListener();

    this._stateChangeUnsubscribe = this._videoSourceStore.onStateChange(
      (state: RendererState) => {
        const isManualInteraction = !this.isPlaying && this.isIframeActive;

        this._stateChangeObserver.handle(
          this as unknown as VideoCreatorStore,
          state,
          { isManualInteraction },
        );

        if (isManualInteraction) {
          this._editorStore.syncExternalChanges(this._prevRendererState, state);
        }

        this._prevRendererState = state;
      },
    );

    logger.info('ClipVideoCreatorAdapter initialized', {
      clipId: this._sessionStore.mainClip.id,
      hasStory: !!options.story,
      hasOrganization: !!options.organization,
    });
  }

  dispose() {
    this._restoreCanvas.cancel();
    this._adaptivePlaybackSourceManager?.dispose();
    this._extraElementDataDisposer?.();
    this._stateChangeUnsubscribe?.();
    this._stateChangeUnsubscribe = null;
    this._prevRendererState = undefined;

    if (this._iframeFocusListener) {
      window.removeEventListener('focus', this._iframeFocusListener, true);
      window.removeEventListener('blur', this._iframeFocusListener, true);
      document.removeEventListener('focusin', this._iframeFocusListener);
      document.removeEventListener('focusout', this._iframeFocusListener);
      this._iframeFocusListener = undefined;
    }
  }

  private setupIframeActiveElementListener(): void {
    const checkIframeActive = () => {
      if (!this._renderer) return;
      const isCurrentlyActive = this._renderer.isIframeActive();
      runInAction(() => {
        this.isIframeActive = isCurrentlyActive;
      });
    };

    this._iframeFocusListener = checkIframeActive;
    window.addEventListener('focus', checkIframeActive, true);
    window.addEventListener('blur', checkIframeActive, true);
    document.addEventListener('focusin', checkIframeActive);
    document.addEventListener('focusout', checkIframeActive);

    checkIframeActive();
  }

  // ============================================================================
  // DELEGATED PROPERTIES (from real VideoCreatorStore)
  // ============================================================================

  get story(): Story | undefined {
    return this._realVideoCreator.story;
  }

  get organization(): Showcase | undefined {
    return this._realVideoCreator.organization;
  }

  get rootStore(): RootStore {
    if (!this._proxiedRootStore) {
      this._proxiedRootStore = createClipRootProxy(
        this._realVideoCreator.rootStore,
        this._editorStore,
        this._videoSourceStore,
      );
    }
    return this._proxiedRootStore;
  }

  get datoClientStore(): DatoClientStore {
    return this._realVideoCreator.datoClientStore;
  }

  get textBrandingService(): TextBrandingService {
    return this._textBrandingService;
  }

  /**
   * Refetch karaoke presets and branding templates for the given aspect ratio.
   * Called during initial renderer setup and when switching between variants.
   */
  async refetchBrandingTemplates(aspectRatio: AspectRatio): Promise<void> {
    await this._textBrandingService.loadKaraokePresets(false);
    this._textBrandingService.getAllBrandingTemplates(
      'karaoke',
      aspectRatio,
      false,
      true,
      null,
    );
  }

  get textProducer(): TextProducer {
    return this._realVideoCreator.textProducer;
  }

  get karaokeProducer(): KaraokeProducer {
    return this._realVideoCreator.karaokeProducer;
  }

  get sceneDetections(): SceneData[] | null {
    return this._realVideoCreator.sceneDetections;
  }

  get faceDetections(): FaceDetection[] | null {
    return this._realVideoCreator.faceDetections;
  }

  get punchListManager(): any {
    return this._realVideoCreator.punchListManager ?? null;
  }

  get flags(): Record<string, boolean | number | string> {
    return {
      ...(this._realVideoCreator.flags || {}),
      ...(this._flags || {}),
    };
  }

  // ============================================================================
  // CORE PROPERTIES
  // ============================================================================

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  // Returns the active variant from the session store.
  // The session store is the single owner of clip variant data.
  get currentVideo(): CurrentLoadedVideo {
    return this._sessionStore.activeVariant as unknown as CurrentLoadedVideo;
  }

  get toasts(): IVideoCreatorStore['toasts'] {
    return this._realVideoCreator.toasts;
  }

  showToast(...args: Parameters<IVideoCreatorStore['showToast']>): number {
    return this._realVideoCreator.showToast(...args);
  }

  updateToast(...args: Parameters<IVideoCreatorStore['updateToast']>): number {
    return this._realVideoCreator.updateToast(...args);
  }

  dismissToast(id: number): void {
    this._realVideoCreator.dismissToast(id);
  }

  clearToasts(): void {
    this._realVideoCreator.clearToasts();
  }

  get duration(): number {
    return this._editorStore.editor?.timeline?.duration ?? 0;
  }

  get time(): number {
    return this._time;
  }

  /**
   * State-only time write. `TranscriptVideoPlayer` assigns `videoCreator.time`
   * directly (its RAF sync loop and `seekTo`); the real `VideoCreatorStore`
   * exposes a writable field, so without this setter that assignment lands on
   * a MobX computed with no setter and throws, killing the player's sync loop
   * (PLAT-10286). Deliberately does NOT forward to the renderer: the RAF loop
   * writes ~60×/s and the renderer (paused behind the review workspace) must
   * not be seek-spammed. Renderer seeks go through `setTime`.
   */
  set time(value: number) {
    this._time = value;
  }

  get state(): RendererState | undefined {
    return this._renderer?.state;
  }

  get renderer(): Renderer | undefined {
    return this._renderer ?? undefined;
  }

  get storyId(): string | undefined {
    return this.story?.id;
  }

  // ============================================================================
  // ASPECT RATIO
  // ============================================================================

  getCurrentAspectRatio(): AspectRatio {
    return (this._sessionStore.selectedDimension as AspectRatio) ?? '16:9';
  }

  getDefaultAspectRatio(): AspectRatio {
    return this._realVideoCreator.getDefaultAspectRatio();
  }

  hasLandscapeAspectRatio(): boolean {
    const ar = this.getCurrentAspectRatio();
    return ar === '16:9';
  }

  hasDefaultAspectRatio(): boolean {
    return true;
  }

  /**
   * Strips template elements from the timeline after a new aspect ratio video
   * is created. Templates have per-aspect configs, so the old elements must be
   * removed and the template re-applied fresh by the UI layer.
   * Mirrors VideoCreatorStore.stripTemplateElementsForNewAspect.
   */
  private stripTemplateElementsForNewAspect(): string | undefined {
    if (!this.currentVideo) return;

    const settings = getElementMetaObject(
      this.currentVideo,
      'video_template_settings',
    );
    if (!settings) return;

    const activeVideoTemplateId = settings.activeVideoTemplateId;
    if (typeof activeVideoTemplateId !== 'string' || !activeVideoTemplateId)
      return;

    if (!Array.isArray(settings.videoTemplateElements)) return;

    const templateElements: Array<{ id: string; type: string }> =
      settings.videoTemplateElements;

    const editor = this._editorStore.editor;

    logger.info(
      'Stripping template elements for fresh aspect ratio application',
      {
        activeVideoTemplateId,
        elementCount: templateElements.length,
      },
    );

    for (const element of templateElements) {
      if (element.type === 'captions') {
        editor.timeline.removeTrack(String(KARAOKE_TRACK_NUMBER));
      } else if (element.id) {
        editor.timeline.removeElement(element.id.toString());
      }
    }

    editor.markDirty('strip_template_for_aspect');

    // Shift remaining elements back to t=0 (reverses intro card ripple)
    const allElements = [...editor.timeline.allElements];
    if (allElements.length > 0) {
      const earliestTime = allElements.reduce(
        (min, el) => Math.min(min, el.time),
        Infinity,
      );
      if (earliestTime > 0) {
        editor.commandShiftMultipleElements(
          allElements.map(e => e.id),
          -earliestTime,
          false,
          false,
        );
      }

      const updatedElements = [...editor.timeline.allElements];
      const maxEndTime = updatedElements.reduce(
        (max, el) => Math.max(max, el.end_time),
        0,
      );
      if (maxEndTime > 0) {
        editor.commandSetTimelineDuration(maxEndTime, false);
      }
    }

    // Clean up per-element metadata by reconstructing extraElementData
    // without the template entries, then writing via the session store.
    const cleanedExtraElementData = {
      ...(this.currentVideo.extraElementData || {}),
    };
    for (const element of templateElements) {
      delete cleanedExtraElementData[element.id.toString()];
    }
    delete cleanedExtraElementData['video_template_settings'];

    const ar = this.getCurrentAspectRatio();
    this._sessionStore.updateVariant(ar, {
      extraElementData: cleanedExtraElementData,
    });

    // Signal the UI to re-apply the template fresh
    return activeVideoTemplateId;
  }

  /**
   * Switch the editor to an already-created variant without tearing down
   * the renderer. Selects the variant in the session store, reloads the
   * editor project, and refetches branding templates for the target AR.
   *
   * Mirrors the `applySnapshot` pattern (undo/redo) which calls
   * `loadProjectForPreview` on the same editor instance.
   */
  private async switchToExistingVariant(
    aspectRatio: AspectRatio,
  ): Promise<void> {
    // Switch the session store to the existing variant
    this._sessionStore.selectDimension(aspectRatio);
    // Reload the editor with the variant's data
    this.reloadActiveVariant(aspectRatio);
    // Refetch branding templates for the target AR
    await this.refetchBrandingTemplates(aspectRatio);
  }

  /**
   * Load the session store's active variant into the preview editor. Shared
   * by the aspect-ratio switch and the audio-first media swaps (PLAT-11445),
   * which change the variant's composition and need the editor to pick it up.
   */
  private reloadActiveVariant(
    aspectRatio: AspectRatio = this.getCurrentAspectRatio(),
  ): void {
    const clip = this._sessionStore.activeVariant;
    const videoSource = clip?.videoSource;
    if (!videoSource) {
      throw new Error('Existing variant is missing video source');
    }
    const preparedVideoSource = this.prepareVideoSourceForEditor(
      videoSource,
      aspectRatio,
    );
    const originalVideoTrackId =
      this._realVideoCreator.pickOriginalVideoTrack(preparedVideoSource) ??
      null;

    this._editorStore.loadProjectForPreview({
      videoSource: preparedVideoSource,
      transcript: (clip.transcriptionSnapshot?.elements ||
        []) as TranscriptElement[],
      originalTranscription:
        this._realVideoCreator.rootStore.transcriptionStore.getOriginalTranscription()
          ?.elements as TranscriptElement[] | undefined,
      karaokeData: clip.karaokeData ?? undefined,
      forcedAlignment:
        this._realVideoCreator.rootStore.transcriptionStore.isForcedAligned,
      // PLAT-10788 — per-language karaoke + language context (DS8/DS9).
      karaokeDataByLanguage: clip.karaokeDataByLanguage,
      transcriptLanguage:
        this._realVideoCreator.rootStore.transcriptionStore.rawLanguage,
      originalVideoLanguage: this.story?.originalVideoLanguage,
      scenes: clip.scenes ?? [],
      originalVideoTrackId,
      extraElementData: clip.extraElementData,
    });
  }

  // ---- PLAT-11445: audio-first clip composition ---------------------------
  // A clip opened while the story's renditions are still producing sources
  // the editor audio (black canvas). These swaps move it onto the session's
  // local original and then onto the preview rendition, in place, without a
  // save: the media is resolved at open (resolveClipMediaSource), so a reload
  // re-resolves anyway (decision Q3).

  /** The story's editor audio URL — what an audio-first element sources. */
  get audioFirstUrl(): string | undefined {
    return this.story?.transcription?.audio?.url ?? undefined;
  }

  /** What the active variant's media elements currently point at. */
  compositionMediaKind(): CompositionMediaKind {
    return classifyCompositionMedia(
      this._sessionStore.activeVariant?.videoSource?.elements,
      this.audioFirstUrl,
    );
  }

  /**
   * Repoint the active variant's matching media elements at `toUrl` and
   * reload the editor. Element ids are refreshed (renderer media cache), so
   * per-element metadata and scene anchors follow them — dropping the scenes
   * would collapse the shot scenes the clip was created with to one per
   * element. Returns false when nothing matched.
   */
  private repointActiveVariant(
    matches: Parameters<typeof repointMediaElements>[1],
    toUrl: string,
  ): boolean {
    const variant = this._sessionStore.activeVariant;
    const source: VideoSource | undefined = variant?.videoSource
      ? deepCloneSafe(variant.videoSource)
      : undefined;
    if (!source?.elements) return false;
    const idMap = repointMediaElements(source.elements, matches, toUrl);
    if (idMap.size === 0) return false;
    this._sessionStore.updateVariant(this.getCurrentAspectRatio(), {
      videoSource: source,
      extraElementData: remapElementMetaIds(variant.extraElementData, idMap),
      scenes: remapSceneAnchorIds(variant.scenes, idMap),
    });
    this.reloadActiveVariant();
    return true;
  }

  /**
   * Play the session's uploaded File on the canvas: push its bytes into the
   * renderer cache under the story's synthetic local URL and point the
   * audio-first elements at it. Decodability is the caller's check.
   */
  async upgradeToLocalCanvas(localFile: File): Promise<boolean> {
    const storyId = this.storyId;
    const renderer = this._renderer;
    if (!storyId || !renderer || requiresNativeLocalPreview(localFile)) {
      return false;
    }
    const localUrl = buildLocalCanvasUrl(storyId);
    await renderer.cacheAsset(localUrl, localFile);
    const audioUrl = this.audioFirstUrl;
    return this.repointActiveVariant(
      el => isAudioFirstSource(el.source, audioUrl),
      localUrl,
    );
  }

  /** Swap every pending-era element (audio / local canvas) onto the rendition. */
  upgradeToRendition(renditionUrl: string): boolean {
    return this.repointActiveVariant(
      isPendingEraElement(this.audioFirstUrl),
      renditionUrl,
    );
  }

  /** Undo the local canvas (renderer never finished loading it). */
  revertLocalCanvasToAudioFirst(): boolean {
    const audioUrl = this.audioFirstUrl;
    if (!audioUrl) return false;
    return this.repointActiveVariant(
      el => isLocalCanvasSource(el.source),
      audioUrl,
    );
  }

  /**
   * Load (or create) a video with the specified aspect ratio.
   * If an associated video with the target AR already exists, returns it
   * for the caller to switch to. If not, creates a new associated video
   * by applying the aspect ratio change command, saves it, and returns
   * the newly created video data.
   *
   * Mirrors the logic from VideoCreatorStore.loadVideoWithAspectRatio
   * and AspectRatiosDropdown.
   */
  async loadVideoWithAspectRatio(aspectRatio: AspectRatio): Promise<void> {
    const currentAspectRatio = this.getCurrentAspectRatio();
    if (currentAspectRatio === aspectRatio) {
      logger.info('loadVideoWithAspectRatio: already at target AR', {
        aspectRatio,
      });
      return;
    }

    // A Clip History preview belongs to the outgoing variant; capturing it
    // below would write the old version into that variant's slot.
    this.versionPreview.exit();

    // Flush current editor state into the session store for the outgoing AR.
    // Must happen before selectDimension / editor overwrite so the variant
    // slot captures the user's latest edits. Without this, the existing-
    // variant branch triggers no save, and the new-variant branch's save
    // operates on the *new* AR — the outgoing AR's edits would be lost.
    await this.prepareCurrentVideoForSave();

    // Flush any pending debounced auto-save so the outgoing variant's edits
    // (just captured above) are persisted to DatoCMS before we overwrite the
    // editor with the target variant. If nothing is pending, flush is a no-op.
    this._debouncedAutoSave.flush();

    this.isLoading = true;

    try {
      // Check if the parent clip already has an associated video for this AR.
      // sessionStore.mainClip is always the normalized parent and has up-to-date
      // associatedVideos (including variants created during this session).
      const parentClip = this._sessionStore.mainClip;
      const existingAssociatedVideo =
        parentClip.aspectRatio === aspectRatio
          ? (parentClip as unknown as AssociatedVideo)
          : parentClip.associatedVideos?.find(
              (v: AssociatedVideo) => v.aspectRatio === aspectRatio,
            );

      if (existingAssociatedVideo) {
        logger.info('loadVideoWithAspectRatio: switching to existing variant', {
          aspectRatio,
          id: existingAssociatedVideo.id,
        });
        await this.switchToExistingVariant(aspectRatio);
      } else {
        logger.info('loadVideoWithAspectRatio: creating new variant', {
          aspectRatio,
          parentId: parentClip.id,
        });
        await this.createNewVideoWithAspectRatio(
          aspectRatio,
          parentClip,
          currentAspectRatio,
        );
      }
      // Reset timeline time to 0 to avoid confusion
      this.setTime(0);
    } catch (error) {
      logger.error('loadVideoWithAspectRatio: failed', error);
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  private async createNewVideoWithAspectRatio(
    aspectRatio: AspectRatioEnum,
    parentClip: VideoClipPreview,
    currentAspectRatio: AspectRatioEnum,
  ) {
    // 1. Create a copy of the current video for the new aspect ratio
    const currentVideoFull = this.currentVideo as unknown as CurrentLoadedVideo;
    const videoSource = this._editorStore.editor.timeline.toStoredJSON();

    // Capture scenes and transcript directly from the editor so the new
    // video carries them over regardless of whether the first save
    // managed to populate the active variant with these fields.
    const previousScenes = this._editorStore.editor.timeline.scenes.map(s =>
      s.toJSON(),
    );
    const previousTranscript =
      this._editorStore.editor.getFullTranscript() as TranscriptElement[];
    const previousKaraokeData = getKaraokeDataFromEditor(
      this._editorStore.editor,
    );

    // Clone current video data for the new AR
    const newVideoData = {
      ...toJS(currentVideoFull),
      videoSource: { ...toJS(videoSource) },
      id: undefined as string | undefined,
      versionId: `unsaved-version-${uuid().substring(0, 8)}`,
      aspectRatio,
      parentVideo: parentClip as CurrentLoadedVideo['parentVideo'],
      associatedVideos: [] as AssociatedVideo[],
      // Clear the thumbnail — it was rendered for the source AR and would
      // appear stretched in the new one. A fresh thumbnail will be generated
      // when this variant is published.
      thumbnail: undefined,
      // Clear publish state — it belongs to the source AR's rendered output.
      // Without this, the new variant appears as "Final" (published) in the
      // dimensions dropdown even though it hasn't been published yet.
      videoFilePrimary: undefined,
      lastPublishJson: undefined,
      _publishedAt: undefined,
      // Explicitly carry over scenes, transcript and karaoke from the
      // previous video so they are never lost during the AR switch.
      scenes: previousScenes,
      transcriptionSnapshot:
        previousTranscript.length > 0
          ? { elements: previousTranscript }
          : (currentVideoFull.transcriptionSnapshot ?? undefined),
      karaokeData:
        previousKaraokeData ?? currentVideoFull.karaokeData ?? undefined,
    };

    // 2. Update internal state to the new video via session store
    this._sessionStore.addVariant(
      newVideoData as unknown as AssociatedVideo & {
        activeVideoTemplateId?: string;
      },
    );
    this._sessionStore.selectDimension(aspectRatio);
    this.currentVideoVersions = [];

    // 3. Adjust video source dimensions for the new aspect ratio
    const [w, h] = (aspectRatio as string).split(':').map(n => parseInt(n));
    const vsHeight =
      newVideoData.videoSource?.height ?? VideoDimensionsHeight.AR_16_9;
    const vsWidth = vsHeight * (w / h);
    if (newVideoData.videoSource) {
      newVideoData.videoSource.width = vsWidth;
    }

    // 4. Get previous standard dimensions BEFORE applying the command
    const standardDimensionsPrev =
      getStandardDimensionsForAspectRatio(currentAspectRatio);

    const karaokeProvider =
      this._textBrandingService.createKaraokePresetProvider();
    const variantService = new KaraokeTemplateVariantService({
      brandingService: this._textBrandingService,
      albumRepository: this.datoClientStore.albumRepository,
    });

    // Wrap ALL mutations (sync + async) in withBulkOperation so that
    // ClipPreviewEditorStore observer notifications are suppressed until
    // the entire aspect-ratio switch completes — single refresh at the end.
    await this._editorStore.withBulkOperation(async () => {
      // 5. Apply aspect ratio change command on the editor
      this._editorStore.editor.commandChangeAspectRatio(
        {
          width: VideoDimensionsWidth.AR_16_9,
          height: VideoDimensionsHeight.AR_16_9,
          aspectRatio: AspectRatioEnum.AR_16_9,
        },
        {
          width: standardDimensionsPrev.width,
          height: standardDimensionsPrev.height,
          aspectRatio: currentAspectRatio,
        },
        {
          width: vsWidth,
          height: vsHeight,
          aspectRatio,
        },
        {}, // naturalMediaDimensions - empty for clip context
        this.textProducer.getBasicTextSettingByAspectRatio(aspectRatio),
        this.karaokeProducer.getKaraokeTextSettingByAspectRatio(aspectRatio),
        karaokeProvider,
        variantService,
      );

      // Sync video source with the current editor state
      newVideoData.videoSource =
        this._editorStore.exportProject().sources.videoSource;

      // 6. Persist karaoke template variant updates
      if (this.organization) {
        const configIdToTemplateIdMap =
          await variantService.ensureVariantsForConfigs(aspectRatio);
        variantService.applyTemplateVariantUpdates(
          this._editorStore.editor,
          this.rootStore.karaokeStore,
          configIdToTemplateIdMap,
        );
      }

      // 7. Strip template elements for fresh re-application
      const activeVideoTemplateId = this.stripTemplateElementsForNewAspect();

      if (activeVideoTemplateId) {
        // Re-apply template with correct AR-specific config directly,
        // before the background save, so the save snapshot includes
        // template elements. The callback is wired by VideoTemplatesProvider.
        if (this.reapplyTemplate) {
          await this.reapplyTemplate(activeVideoTemplateId);
        } else {
          this.pendingTemplateReapplyId = activeVideoTemplateId;
        }
      }

      // Sync video source with the current editor state
      newVideoData.videoSource =
        this._editorStore.exportProject().sources.videoSource;

      // 8. Reload default template for the new aspect
      this._textBrandingService.loadDefaultTemplateOnVideoLoad();

      // 9. Create scenes from detection and auto-frame for the new AR
      //     Mirrors VideoCreatorStore.performCreateScenesFromDetection +
      //     performAutoFrameNewAspectRatio (lines 1675-1680)
      await this.performCreateScenesForNewAspectRatio(aspectRatio);
    });

    // 10. Save the new video (now background, non-blocking).
    //     Creates it in Dato and links to parent. publishVideo will
    //     await waitForBackgroundSave() to ensure the ID is assigned
    //     before publish.
    this.saveStoryAndVideo(false, true, true, false, true);

    logger.info('loadVideoWithAspectRatio: new AR video creation dispatched', {
      aspectRatio,
    });
  }

  /**
   * Delegate clip creation prep to the real VideoCreatorStore.
   * Used by clip save flows that construct a new video from the current clip.
   */
  prepareAsCurrentLoadedVideo(
    originalVideo: Video,
    title: string,
    sourcePlatform: SourcePlatform,
  ) {
    return this._realVideoCreator.prepareAsCurrentLoadedVideo(
      originalVideo,
      title,
      sourcePlatform,
    );
  }

  /**
   * Delegate adding saved clips to the story to the real store.
   */
  addMultipleVideosToStory(videos: Video[]): Promise<void> {
    return this._realVideoCreator.addMultipleVideosToStory(videos);
  }

  /**
   * In clip preview context, loadVideo is a no-op. The adapter already holds
   * the clip state and should not swap the underlying preview.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  async loadVideo(_videoId: string, _resetTimeline?: boolean): Promise<void> {
    logger.debug('loadVideo:noop:clip-preview');
  }

  /**
   * Create scenes from detection data and auto-frame for a new aspect ratio.
   * Mirrors VideoCreatorStore.performCreateScenesFromDetection() +
   * performAutoFrameNewAspectRatio().
   */
  private async performCreateScenesForNewAspectRatio(
    aspectRatio: AspectRatio,
  ): Promise<void> {
    const storyId = this.story?.id;
    const editor = this._editorStore.editor;

    if (!storyId || !editor) {
      logger.warn(
        'performCreateScenesForNewAspectRatio: missing storyId or editor',
      );
      return;
    }

    // 1. Read pre-loaded scene detections from the real VideoCreatorStore
    //    (populated during initializeWithStory). Fall back to API if unavailable.
    let sceneDetections: SceneData[] | null =
      this._realVideoCreator.sceneDetections ?? null;
    if (!sceneDetections) {
      try {
        sceneDetections = await fetchParsedScenesApi(storyId);
      } catch (error) {
        logger.warn(
          'performCreateScenesForNewAspectRatio: failed to fetch scene detections',
          { error },
        );
      }
    }

    // 2. Read pre-loaded natural media dimensions, with async fallback.
    const naturalMediaDimensions =
      Object.keys(this._realVideoCreator.preloadedNaturalDimensions).length > 0
        ? this._realVideoCreator.preloadedNaturalDimensions
        : await this._realVideoCreator.getNaturalMediaDimensions();

    await createScenesFromDetection(
      storyId,
      editor,
      sceneDetections,
      async (sid: string) => {
        try {
          sceneDetections = await fetchParsedScenesApi(sid);
        } catch (err) {
          logger.warn(
            'performCreateScenesForNewAspectRatio: preload fallback failed',
            { error: err },
          );
        }
      },
      naturalMediaDimensions,
    );

    // 3. Auto-frame all scenes if the feature flag is enabled

    if (this._flags?.enableAutoFraming) {
      // Read pre-loaded face detections from the real VideoCreatorStore
      // (populated during initializeWithStory). Fall back to API if unavailable.
      let faceDetections: FaceDetection[] | null =
        this._realVideoCreator.faceDetections ?? null;
      if (!faceDetections) {
        try {
          const result = await fetchFaceDetectionsApi(storyId);
          faceDetections = result?.detections ?? null;
        } catch (error) {
          logger.warn(
            'performCreateScenesForNewAspectRatio: failed to fetch face detections',
            { error },
          );
        }
      }

      await autoFrameNewAspectRatio(aspectRatio, {
        editor,
        editorLayoutConfig: this._editorStore.getPreviewLayoutConfig(),
        faceDetections,
        sceneManipulationLimit:
          this._flags?.sceneManipulationLimit ??
          DEFAULT_SCENE_MANIPULATION_LIMIT,
        preloadFaceDetections: async (sid: string) => {
          try {
            const result = await fetchFaceDetectionsApi(sid);
            faceDetections = result?.detections ?? null;
          } catch (err) {
            logger.warn(
              'performCreateScenesForNewAspectRatio: face preload fallback failed',
              { error: err },
            );
          }
        },
        storyId,
        autoFramingStrategy: this._flags?.autoFramingStrategy,
        shots: sceneDetections,
        naturalMediaDimensions,
      });
    }

    logger.info('performCreateScenesForNewAspectRatio: complete', {
      aspectRatio,
    });
  }

  /**
   * Get the maximum track number from all elements, excluding karaoke track
   */
  getMaxTrack(): number {
    const tracks = this._editorStore.editor.timeline.allElements
      .map(element => element.track ?? 0)
      .map(track => (track < KARAOKE_TRACK_NUMBER ? track : 0));
    return Math.max(...tracks, 1);
  }

  // ============================================================================
  // TIME OPERATIONS
  // ============================================================================

  async setTime(time: number): Promise<void> {
    this._time = time;
    if (this._renderer) {
      // PLAT-11119 — the renderer only holds the windowed slice of the
      // timeline, so a seek outside it lands on footage it was never sent.
      // Rebuild the window around the destination first. Skipped while
      // scrubbing: a drag issues seeks continuously and has its own debounced
      // window reaction.
      if (!this.isScrubbing) {
        // Awaiting here lets a later seek overtake an earlier one whose window
        // is still rebuilding, and the loser would land last and win. Seeks
        // were issued synchronously before this guard existed, so only the
        // newest may command the renderer.
        const seek = ++this._seekSeq;
        await this._editorStore.ensureWindowCovers(time);
        if (seek !== this._seekSeq) return;
      }
      await this._renderer.setTime(time);
    }
  }

  async setDuration(duration: number | null): Promise<void> {
    // Duration is derived from video source, this is a no-op for clips
    this._editorStore.editor?.commandSetTimelineDuration(duration, false);
  }

  // ============================================================================
  // ELEMENT OPERATIONS
  // ============================================================================

  getActiveElement(): ElementState | undefined {
    if (!this._renderer || this.activeElementIds.length === 0) {
      return undefined;
    }
    const id = this.activeElementIds[0];
    return this._findElementById(id);
  }

  private _findElementById(id: string): ElementState | undefined {
    return this._renderer?.findElement(
      element => element.source.id === id,
      this._prevRendererState,
    );
  }

  getRealActiveElement(): ElementState | undefined {
    return this.getActiveElement();
  }

  async setActiveElements(...elementIds: string[]): Promise<void> {
    const selectable = elementIds;
    runInAction(() => {
      this.activeElementIds = selectable;
    });
    await this._renderer?.setActiveElements(selectable);
    await this._renderer?.setActiveComposition(null);
  }

  async handleActiveElementsChangeFromRenderer(
    rendererElementIds: string[],
  ): Promise<void> {
    const elementIds = rendererElementIds;
    this.activeElementIds = elementIds;

    if (elementIds.length === 0) {
      try {
        await this._renderer?.setActiveComposition(null);
      } catch (err) {
        logger.warn('Failed to reset active composition on deselect', {
          error: extractErrorInfo(err),
        });
      }
      return;
    }

    const newActiveElement = this.getActiveElement();
    if (!newActiveElement) return;

    try {
      this._sceneLayoutSelectionManager.reselectInnerElement(
        newActiveElement,
        innerId => {
          void this._activateInnerInComposition(innerId);
        },
      );
    } catch (err) {
      logger.warn('reselectInnerElement failed', {
        error: extractErrorInfo(err),
      });
    }
  }

  /**
   * Select `innerId` in the iframe. If it's nested inside a Path B
   * composition wrapper, dive into that composition first — otherwise
   * Creatomate can't target the nested id and echoes `[null]`.
   */
  private async _activateInnerInComposition(innerId: string): Promise<void> {
    const representationRecord =
      this._editorStore.sceneLayoutIndexStore.getRepresentationRoleRecordFor(
        innerId,
        this._time,
      );
    const compositionId = representationRecord?.link.compositionId ?? null;

    if (!compositionId) {
      await this.setActiveElements(innerId);
      return;
    }

    this.activeElementIds = [innerId];
    try {
      await this._renderer?.setActiveComposition(compositionId);
      await this._renderer?.setActiveElements([innerId]);
    } catch (err) {
      logger.warn('Failed to dive into composition for inner selection', {
        error: extractErrorInfo(err),
        compositionId,
        innerId,
      });
    }
  }

  // ============================================================================
  // UNDO/REDO (Snapshot-based)
  // ============================================================================

  get canUndo(): boolean {
    return !this.versionPreview.isPreviewing && this.undoSnapshots.length > 0;
  }

  get canRedo(): boolean {
    return !this.versionPreview.isPreviewing && this.redoSnapshots.length > 0;
  }

  getUndoLabel(): string {
    return this.undoSnapshots[this.undoSnapshots.length - 1]?.label ?? '';
  }

  getRedoLabel(): string {
    return this.redoSnapshots[this.redoSnapshots.length - 1]?.label ?? '';
  }

  undo(): void {
    if (this.versionPreview.isPreviewing) return;
    const previous = this.undoSnapshots.pop();
    if (!previous) return;

    this.redoSnapshots.push(this.buildSnapshot(previous.label));
    if (this.redoSnapshots.length > MAX_UNDO_REDO_STACK_SIZE) {
      this.redoSnapshots.shift();
    }

    this.applySnapshot(previous);
    this._editorStore?.editor?.notifyObservers(EditorChangeType.EXTERNAL_UNDO);
    this._debouncedAutoSave(true);
  }

  redo(): void {
    if (this.versionPreview.isPreviewing) return;
    const next = this.redoSnapshots.pop();
    if (!next) return;

    this.undoSnapshots.push(this.buildSnapshot(next.label));
    if (this.undoSnapshots.length > MAX_UNDO_REDO_STACK_SIZE) {
      this.undoSnapshots.shift();
    }

    this.applySnapshot(next);
    this._editorStore?.editor?.notifyObservers(EditorChangeType.EXTERNAL_REDO);
    this._debouncedAutoSave(true);
  }

  /**
   * Refuse an edit to a previewed version, say why, and put the canvas back:
   * a canvas drag has already moved the element in the renderer, and the
   * refused command leaves nothing to push the model back. Once the burst
   * ends — a drag is many commands, and pushing mid-drag would pull the
   * element from under the pointer.
   */
  private refuseEditWhilePreviewing(command: Command): void {
    logger.info('Edit ignored while previewing a saved version', {
      command: command?.getName?.(),
    });
    this.versionPreview.explainPausedEdit();
    this._restoreCanvas();
  }

  /** The Clip History preview, wired to this clip's editor and save queue. */
  private createVersionPreview(): ClipVersionPreviewStore {
    return new ClipVersionPreviewStore({
      loadVersion: async version => {
        const storyId = this.story?.id;
        if (!storyId) return err('PRECONDITION', 'No story is loaded');
        return this._versionHistoryService.loadVersion({ storyId, version });
      },
      landPendingSave: () => this.landPendingSave(),
      buildSnapshot: () => this.buildSnapshot(),
      applySnapshot: snapshot => {
        this.applySnapshot(snapshot);
        this._editorStore?.editor?.notifyObservers(
          EditorChangeType.EXTERNAL_UNDO,
        );
      },
      restore: current => {
        this.undoSnapshots.push({ ...current, label: 'Restore version' });
        if (this.undoSnapshots.length > MAX_UNDO_REDO_STACK_SIZE) {
          this.undoSnapshots.shift();
        }
        this.redoSnapshots = [];
        this._pendingSaveReasons.add('restore_version');
        this._debouncedAutoSave();
      },
      showToast: toast => this.showToast(toast),
      dismissToast: id => this.dismissToast(id),
    });
  }

  setShowSubtitles(show: boolean) {
    this.showSubtitles = show;
  }

  /**
   * PLAT-11689 — Captions picked in the transcript's view dropdown: the
   * caption-line view, and the captions shown on the video if Cap had hidden
   * them (undoable and autosaved, like pressing Cap). Same pick as Creator
   * Studio's `openCaptionsView`.
   */
  openCaptionsView() {
    this.setShowSubtitles(true);
    const editor = this._editorStore?.editor;
    // PLAT-11877 — with no caption track yet, Captions is also the way to
    // make one: produce it in the showcase's default style (the one rule,
    // `getDefaultCaptionTemplateConfig`), shown. Leaving the view again never
    // takes the captions off the video.
    if (editor && editor.getKaraokeLines().length === 0) {
      const fallbackTemplate =
        this.textBrandingService.getDefaultCaptionTemplateConfig(
          this.getCurrentAspectRatio(),
        );
      if (fallbackTemplate) {
        this.textBrandingService.selectedTemplateId =
          fallbackTemplate.templateId;
      }
      if (
        produceFirstCaptions({
          editor,
          fallbackTemplate,
          transcriptLanguage:
            this._realVideoCreator.rootStore.transcriptionStore.rawLanguage,
          originalVideoLanguage: this.story?.originalVideoLanguage,
          showToast: toast => this.showToast(toast),
        })
      ) {
        this._editorStore.refreshKaraokeState();
        this.getVideoSourceStore()?.refreshSource();
      }
      return;
    }
    if (editor && areCaptionsHidden(editor)) {
      setCaptionsHidden(editor, false);
      this._editorStore.refreshKaraokeState();
      this.getVideoSourceStore()?.refreshSource();
    }
  }

  /**
   * PLAT-11054 — rename a reel Section.
   *
   * Names live in `clipJson.reel`, not the timeline (DE13), so this is not an
   * editor command and gets no timeline undo entry (DS3). That also means
   * nothing routes through the patched `executeCommand` above, so the debounced
   * auto-save has to be scheduled here — otherwise a rename would sit in memory
   * until some unrelated edit happened to save it.
   *
   * The write lands on the variant, which is what `prepareCurrentVideoForSave`
   * reads and spreads, so the name rides along with the next save of the clip.
   */
  renameReelSection(sectionId: string, name: string): void {
    // The name itself is the creator's copy and would only be noise in a log;
    // what a support trail needs is which Section changed, and whether it was
    // cleared back to the fallback.
    this._updateReel(
      'renameReelSection',
      { sectionId, cleared: !name.trim() },
      reel => renameSectionInReel(reel, sectionId, name),
    );
  }

  /**
   * PLAT-11059 — a Section named by the AI.
   *
   * The same write as {@link renameReelSection} with the provenance that decides
   * what a later generation may overwrite (DE10): `'generated'` may be replaced
   * without asking, a name the creator typed may not.
   */
  setGeneratedReelSectionName(sectionId: string, name: string): void {
    this._updateReel('setGeneratedReelSectionName', { sectionId }, reel =>
      renameSectionInReel(reel, sectionId, name, 'generated'),
    );
  }

  /**
   * Every write to the half of a reel the timeline cannot carry — Section names
   * and chapters both (DE13).
   *
   * None of these is an editor command: they do not touch the timeline, so they
   * get no undo entry (DS3) and there is nothing for the renderer to repaint (a
   * Chapter renders nothing on the video, DE14). That also means nothing routes
   * through the patched `executeCommand` above, so the debounced auto-save has
   * to be scheduled here — otherwise a rename would sit in memory until some
   * unrelated edit happened to save it. No argument to it: these are edits the
   * creator made, so they record a video action like any other, unlike undo/redo
   * which pass `true` to skip it.
   *
   * The write lands on the variant, which is what `prepareCurrentVideoForSave`
   * reads and spreads, so it rides along with the next save of the clip.
   *
   * @param options.extra Further `clipJson` fields to write in the same pass —
   *   read before `reel`, so a caller cannot accidentally clobber it
   * @param options.level `debug` for a write that is not worth a production log
   *   line: a view toggle a creator may hit a dozen times in a sitting, as
   *   against an edit to the reel's own content
   * @param options.undoLabel Records an undo entry for this write, for the one
   *   caller whose gesture no command backs (see `moveReelSectionChapters`).
   *   Left off everywhere else: a rename or an inserted chapter is undone by
   *   editing it back, not by rewinding the clip (DS3)
   */
  private _updateReel(
    event: string,
    detail: Record<string, unknown>,
    mutate: (reel: Reel | undefined) => Reel,
    options?: {
      extra?: Partial<ClipMetadata>;
      level?: 'info' | 'debug';
      undoLabel?: string;
    },
  ): Reel | undefined {
    const clipJson = this.currentVideo?.clipJson as ClipMetadata | undefined;
    if (!clipJson) {
      logger.warn(`${event}: variant has no clipJson`, detail);
      return undefined;
    }

    // Returned so a caller can tell what the write actually did by comparing it
    // against the reel it passed in — read from the result rather than by
    // re-applying the rule the mutation followed (PLAT-11063).
    const next = mutate(clipJson.reel);

    // PLAT-11146 — taken before the write, so the snapshot holds the anchors as
    // they were, which is what an undo reads them back from (PLAT-11075 DS8).
    // Only when the write moves one: a drop that nets out to nothing would
    // otherwise leave an entry that appears to do nothing when it is used.
    if (options?.undoLabel && chapterAnchorsChanged(clipJson.reel, next)) {
      this.saveVideoStateSnapshot('undo', options.undoLabel);
    }

    logger[options?.level ?? 'info'](event, {
      clipId: this.currentVideo?.id,
      ...detail,
    });

    this._sessionStore.updateVariant(this.getCurrentAspectRatio(), {
      clipJson: {
        ...clipJson,
        ...options?.extra,
        reel: next,
      },
    });
    // PLAT-11063 — the `event` string already names the edit precisely enough
    // for the save event to report what it flushed.
    this._pendingSaveReasons.add(event);
    this._debouncedAutoSave();
    return next;
  }

  /** How many boundaries the reel holds right now, for the two writes below. */
  private _chapterCount(): number {
    const clipJson = this.currentVideo?.clipJson as ClipMetadata | undefined;
    return clipJson?.reel?.chapters.length ?? 0;
  }

  /**
   * Opens a chapter at `sectionId`. `firstSectionId` is the reel's opening
   * Section, which the first boundary inserted below the top also claims so
   * that nothing is left ahead of every boundary (DS3).
   *
   * @returns how many boundaries the write opened: 0 when one already starts
   *   here, 1 normally, and 2 when DS3's opening chapter came with it. The panel
   *   counts `reel_chapter_created` off this rather than re-deriving
   *   `insertChapterInReel`'s rules, which would be the same rule in two places
   *   and free to drift (PLAT-11063).
   */
  insertReelChapter(sectionId: string, firstSectionId: string): number {
    const before = this._chapterCount();
    const next = this._updateReel('insertReelChapter', { sectionId }, reel =>
      insertChapterInReel(reel, sectionId, firstSectionId),
    );
    return next ? next.chapters.length - before : 0;
  }

  /**
   * Opens an empty chapter past the last Section, anchored to the Section it
   * trails so that Sections added there later fall into it (DS7).
   *
   * @returns how many boundaries the write opened, as `insertReelChapter`.
   */
  appendReelChapter(afterSectionId: string, firstSectionId: string): number {
    const before = this._chapterCount();
    const next = this._updateReel(
      'appendReelChapter',
      { afterSectionId },
      reel => appendChapterToReel(reel, afterSectionId, firstSectionId),
    );
    return next ? next.chapters.length - before : 0;
  }

  /**
   * PLAT-11099 — the whole chapter set, replaced by one AI run (E8).
   *
   * One write, not a remove-then-insert loop: the run produced one structure and
   * the reel should never be observed half way between the old set and the new
   * one. `applyChapterization` mints the ids and carries the Section names and
   * the off Sections across; nothing else about the reel is touched.
   *
   * The creator was told this destroys the chapters they had, in the overlay
   * that specified the run (DS7), so this method needs no protection of its own.
   */
  replaceReelChapters(
    boundaries: readonly ChapterBoundary[],
    preservedTitleSources?: ReadonlyMap<string, NameSource>,
  ): void {
    this._updateReel(
      'replaceReelChapters',
      { chapterCount: boundaries.length },
      reel => applyChapterization(reel, boundaries, preservedTitleSources),
    );
  }

  renameReelChapter(chapterId: string, title: string): void {
    // The title is the creator's copy and would only be noise in a log; what a
    // support trail needs is which chapter changed, and whether it was cleared.
    this._updateReel(
      'renameReelChapter',
      { chapterId, cleared: !title.trim() },
      reel => renameChapterInReel(reel, chapterId, title),
    );
  }

  /**
   * PLAT-11064 — a Chapter titled by the AI.
   *
   * {@link renameReelChapter} with the provenance DE10 protects: a title the
   * creator typed is `'user'` and is never replaced by a generation aimed
   * elsewhere; this one may be.
   */
  setGeneratedReelChapterTitle(chapterId: string, title: string): void {
    this._updateReel('setGeneratedReelChapterTitle', { chapterId }, reel =>
      renameChapterInReel(reel, chapterId, title, 'generated'),
    );
  }

  /**
   * PLAT-11056 — folds a chapter away, or opens it back up. Persisted so a
   * collapsed chapter is still collapsed on the next visit (DS3).
   */
  setReelChapterCollapsed(chapterId: string, collapsed: boolean): void {
    // A view toggle rather than an edit to the reel's content, and one a creator
    // may hit repeatedly while working — so it does not earn a production log
    // line, only a line while debugging.
    this._updateReel(
      'setReelChapterCollapsed',
      { chapterId, collapsed },
      reel => setChapterCollapsed(reel, chapterId, collapsed),
      { level: 'debug' },
    );
  }

  /**
   * PLAT-11072 — a chapter that owns nothing, dragged to a new gap. No timeline
   * edit and so no command: what moves is the anchor it renders at.
   */
  moveEmptyReelChapter(chapterId: string, afterSectionId: string): void {
    this._updateReel(
      'moveEmptyReelChapter',
      { chapterId, afterSectionId },
      reel => moveEmptyChapterInReel(reel, chapterId, afterSectionId),
    );
  }

  removeReelChapter(chapterId: string): void {
    this._updateReel('removeReelChapter', { chapterId }, reel =>
      removeChapterFromReel(reel, chapterId),
    );
  }

  /**
   * PLAT-11204 — every chapter at once, in one write (DS1). The Sections and
   * their names are carried across; only the structure over them goes.
   *
   * The creator was told what this destroys, in the confirm that fired it (DS2),
   * so this method needs no protection of its own — the same reasoning
   * {@link replaceReelChapters} takes.
   */
  removeAllReelChapters(): void {
    this._updateReel(
      'removeAllReelChapters',
      { chapterCount: this._chapterCount() },
      reel => removeAllChaptersFromReel(reel),
    );
  }

  /**
   * PLAT-11075 — the boundary a drag moved, if it moved one at all.
   *
   * Membership is derived from the row order (DE12), so most drops write
   * nothing here; the write is for the two that do — a chapter losing the
   * Section it was anchored to, and a chapter gaining a new first Section.
   * `rowIds` is the order *before* the move, which is where a departing
   * chapter's next Section is read from.
   *
   * PLAT-11146 — `ownsUndoEntry` is for the drop whose *whole* edit is the
   * boundary: a Section dragged across the boundary beside it keeps its position
   * in playback order, so no command runs and nothing takes the snapshot the
   * undo stack is built from. Without one that drag cannot be undone — and
   * worse, the next Undo takes back an older edit and sweeps this up with it,
   * since restoring anchors reads them from that older snapshot (PLAT-11075
   * DS8). Every other drop is already covered by the command that moved the
   * timeline, and must not record a second entry over the top of it.
   */
  moveReelSectionChapters(
    rowIds: readonly string[],
    move: ReelSectionMove,
    options?: { ownsUndoEntry?: boolean },
  ): void {
    this._updateReel(
      'moveReelSectionChapters',
      { sectionId: move.sectionId, chapterId: move.chapterId },
      reel => moveSectionInReelChapters(reel, rowIds, move),
      options?.ownsUndoEntry
        ? { undoLabel: 'Move section between chapters' }
        : undefined,
    );
  }

  /**
   * PLAT-11071 — the stored half of a Section swap: the departing Section's name
   * entry goes, a hand-typed one moves across to the replacement, and a chapter
   * that opened on the departing Section is repointed at it.
   *
   * `isReel: true` rides along for the same reason an insert sets it: a swap
   * leaves a clip holding a passage that is not its own single range, and the
   * save path only re-derives `subClips` for a reel — leave it unmarked and a
   * reload brings back the passage the clip started as.
   */
  replaceReelSection(replacement: ReelSectionReplacement): void {
    this._updateReel(
      'replaceReelSection',
      {
        removedSectionId: replacement.removedSectionId,
        addedSectionIds: replacement.added.map(section => section.sectionId),
      },
      reel => replaceSectionInReel(reel, replacement),
      { extra: { isReel: true } },
    );
  }

  /**
   * PLAT-11060 — the boundary a removal moves.
   *
   * A removal is the *departure* half of a drag and nothing else: the Section
   * has left the reel, so a chapter anchored to it re-anchors to the Section
   * that followed — or, with nothing left under it, keeps its place as an empty
   * holder (PLAT-11075 DS3). Nothing arrives, so `opensChapter` is never set and
   * `moveSectionInReelChapters` runs its departure half alone rather than this
   * having a second copy of that reasoning.
   *
   * Not cosmetic: `deriveReelChapterGroups` resolves a chapter by its
   * `firstSectionId`, so a mid-reel chapter whose opening Section is removed
   * would otherwise drop out of the render entirely, title and all.
   *
   * `rowIds` is the order *before* the removal, which is where the next Section
   * down is read from.
   */
  removeReelSectionChapters(
    rowIds: readonly string[],
    sectionId: string,
  ): void {
    this._updateReel('removeReelSectionChapters', { sectionId }, reel =>
      moveSectionInReelChapters(reel, rowIds, { sectionId }),
    );
  }

  /**
   * PLAT-11072 — the stored half of turning a Section off (DS2).
   *
   * The passage has just left the timeline, so this is what it takes to put it
   * back: its media range, its name, its lineage and the row it sat after. The
   * name moves out of `reel.sections` and into the entry, which is what keeps
   * the save's `pruneReelSectionNames` from dropping it as a name for a Section
   * the timeline no longer has.
   *
   * `isReel: true` rides along for the same reason an insert sets it — a clip
   * whose Sections can be turned off is a reel, and only a reel re-derives its
   * `subClips` on save.
   */
  disableReelSection(input: DisableReelSectionInput): void {
    this._updateReel(
      'disableReelSection',
      {
        sectionId: input.section.id,
        afterSectionId: input.afterSectionId,
      },
      reel => disableSectionInReel(reel, input),
      { extra: { isReel: true } },
    );
  }

  /**
   * PLAT-11072 — the stored half of turning a Section back on (DS2).
   *
   * The re-insert has already rebuilt the passage under a **new** provenance id,
   * so the entry is dropped and everything that pointed at the old id is
   * repointed at the new one (DS12).
   *
   * Lineage (PLAT-11067) needs one thing more. It lives in `subClips`, which the
   * save re-derives from the timeline — so a save taken while the Section was
   * off dropped its entry. The stored `origin` is written back as a sub-clip at
   * the passage's media range, which is what the next re-derive matches on.
   */
  enableReelSection(sectionId: string, newSectionId: string): void {
    const clipJson = this.currentVideo?.clipJson as ClipMetadata | undefined;
    const entry = clipJson?.reel?.disabledSections?.find(
      section => section.id === sectionId,
    );
    const origin = entry?.origin?.length
      ? {
          subClips: [
            ...(clipJson?.subClips ?? []),
            {
              startTime: entry.range.start,
              duration: Math.max(0, entry.range.end - entry.range.start),
              origin: entry.origin,
            },
          ],
        }
      : undefined;

    this._updateReel(
      'enableReelSection',
      { sectionId, newSectionId },
      reel => enableSectionInReel(reel, sectionId, newSectionId),
      { extra: { isReel: true, ...origin } },
    );
  }

  /**
   * PLAT-11072 — Remove from Reel on a row that is already off (DS7). There is
   * no timeline to change and so no command: the entry is the whole of it.
   */
  removeDisabledReelSection(sectionId: string): void {
    this._updateReel('removeDisabledReelSection', { sectionId }, reel =>
      removeDisabledSectionFromReel(reel, sectionId),
    );
  }

  /**
   * PLAT-11072 DS10 — every off row's anchor re-read off the row order a drag
   * has just produced, so a row that is off keeps the place the creator can see.
   *
   * Skipped entirely when nothing moved: the service returns the same reel, and
   * writing it back would schedule a save for an edit that did not happen.
   */
  reanchorDisabledReelSections(orderedRowIds: readonly string[]): void {
    const clipJson = this.currentVideo?.clipJson as ClipMetadata | undefined;
    const reel = clipJson?.reel;
    if (!reel?.disabledSections?.length) return;
    if (reanchorDisabledSections(reel, orderedRowIds) === reel) return;

    this._updateReel(
      'reanchorDisabledReelSections',
      { count: reel.disabledSections.length },
      current => reanchorDisabledSections(current, orderedRowIds) ?? EMPTY_REEL,
      { level: 'debug' },
    );
  }

  /**
   * PLAT-11057 — records the names Sections just inserted into the reel take
   * from their clips (DE8).
   *
   * The Sections themselves are the insert command's doing; this is only the
   * half of a reel that the timeline cannot carry (DE13), so like a rename it
   * is not a command and schedules its own save.
   */
  recordInsertedReelSections(added: readonly AddedReelSection[]): void {
    this._updateReel(
      'recordInsertedReelSections',
      { sectionIds: added.map(section => section.sectionId) },
      reel => addSectionsToReel(reel, added),
      // A clip that has been added to holds more than one passage, which is
      // what a reel is (DE15). Marking it is not cosmetic: the save path only
      // re-derives `subClips` for a reel, and `subClips` is what a lightweight
      // clip is re-cropped from — leave a grown clip unmarked and it comes back
      // from a reload as the single passage it started as.
      { extra: { isReel: true } },
    );
  }

  // ============================================================================
  // SAVE OPERATIONS
  // ============================================================================

  /**
   * Save story and video (main save entry point).
   *
   * After the background-save refactor this method is non-blocking: it
   * snapshots the editor state and delegates to saveCurrentAndParentVideos
   * which fires the heavy IO in the background. Callers that need the save
   * to finish (e.g. publishVideo) should follow up with
   * `await this.waitForBackgroundSave()`.
   */
  async saveStoryAndVideo(
    asFinal: boolean = false,
    withRenderer = true,
    resetTimeline = true,
    autoSave = false,
    skipLastActionUpdate = false,
  ) {
    // Cancel any pending debounced auto-save so it doesn't fire after this
    // direct save, which would cause a redundant second save.
    this._debouncedAutoSave.cancel();

    // A Clip History preview is never saved; Restore saves once it ends.
    if (this.versionPreview.isPreviewing) {
      logger.info('Save skipped while previewing a saved version');
      return;
    }

    const videoSource = this._editorStore.editor.timeline.toStoredJSON();
    this._sessionStore.updateVariant(this.getCurrentAspectRatio(), {
      videoSource,
    });

    if (asFinal) {
      if (!this.story) {
        logger.warn('TYPE_ERROR: story is undefined');
        throw new Error('Cannot save story: story not set');
      }
      this.story.finalVideo =
        (this.currentVideo as any)?.parentVideo ?? this.currentVideo;
    }

    this.saveCurrentAndParentVideos(
      withRenderer,
      resetTimeline,
      autoSave,
      skipLastActionUpdate,
    );
  }

  /**
   * Save current and parent videos (background, fire-and-forget).
   *
   * Mirrors VideoCreatorStore.saveCurrentAndParentVideos: guards re-entrancy
   * via `isBackgroundSaving`, queues at most one pending request, then
   * dispatches the heavy IO via `_runBackgroundSave` without awaiting it.
   */
  async saveCurrentAndParentVideos(
    withRenderer = true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    resetTimeline = true,
    autoSave = false,
    skipLastActionUpdate = false,
  ) {
    // PLAT-11063 — consumed before the early returns below, so a save that never
    // starts cannot leave the flag standing for whichever save runs next and
    // have that one report itself as the queued one.
    const queued = this._nextSaveQueued;
    this._nextSaveQueued = false;

    // Early returns for exceptional cases
    if (
      !this.currentVideo ||
      !this.story ||
      !this.datoClientStore.videoRepository ||
      !this.datoClientStore.storyRepository
    ) {
      logger.error('saveCurrentAndParentVideos:skip:missing-deps');
      return;
    }

    // If a background save is already in-flight, queue this request as pending
    if (this.isBackgroundSaving) {
      this._pendingNextSave = { withRenderer, autoSave };
      logger.debug('saveCurrentAndParentVideos:queued-pending');
      return;
    }

    this.isBackgroundSaving = true;
    this.savingError = undefined;

    if (!skipLastActionUpdate) {
      this.addVideoAction({
        editor: this.rootStore.userIdentityStore.currentEditor,
        date: new Date().getTime(),
        type: autoSave ? 'autosave' : 'save',
      });
    }

    // Fire and forget — do NOT await
    void this._runBackgroundSave(withRenderer, autoSave, queued);
  }

  /**
   * Wait for the current background save to complete.
   * Resolves immediately if no save is in-flight.
   * Rejects if the save fails.
   */
  waitForBackgroundSave(): Promise<void> {
    if (!this.isBackgroundSaving) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      this._backgroundSaveResolvers.push({ resolve, reject });
    });
  }

  /**
   * PLAT-11911: run a pending debounced auto-save now and resolve once every
   * save has landed. Rejects if a save fails.
   *
   * Loops because a save queued behind an in-flight one fires only after that
   * one's waiters resolve.
   */
  async flushPendingSave(): Promise<void> {
    // Callers hand the session off next (modal close, Edit in Creator Studio):
    // it must hold the live clip, not a Clip History preview.
    this.versionPreview.exit();
    await this.landPendingSave();
  }

  /**
   * Before a publish. A Clip History version being viewed is published the way
   * Creator Studio publishes an opened one: it becomes the clip (Restore), and
   * its save lands first, since the publish re-reads the transcript from the
   * saved record.
   */
  async prepareForPublish(): Promise<void> {
    if (!this.versionPreview.isPreviewing) return;
    this.versionPreview.commit();
    await this.flushPendingSave();
  }

  /** Run a pending debounced auto-save now and wait for every save to land. */
  private async landPendingSave(): Promise<void> {
    this._debouncedAutoSave.flush();
    while (this.isBackgroundSaving) {
      await this.waitForBackgroundSave();
    }
  }

  /**
   * Register the dub run a press of the Voice control just started, so that a
   * publish made while it is out can wait for it.
   *
   * The run's own outcome is not this object's business — the Voice control
   * already reports its failures. All that is kept here is WHEN it finished,
   * so a rejected run is folded into a plain resolve rather than left as an
   * unhandled rejection on a second promise.
   *
   * Only the latest run is held. Two runs cannot overlap while one is genuinely
   * out, because the Voice control drops a press made in that window. A run this
   * object has GIVEN UP on is different: the control is free again the moment it
   * is cancelled, so a newer run can be registered here while the older read is
   * still on the wire. That is what the identity checks are for.
   *
   * `cancel` is required rather than optional because a run registered without
   * one cannot be given up, and a dub given up on would then still land on the
   * clip after a publish had gone out without it.
   */
  trackDubRun(run: Promise<unknown>, cancel: () => void): void {
    const settled = run.then(
      () => undefined,
      () => undefined,
    );
    this._pendingDubRun = settled;
    this._cancelPendingDubRun = cancel;
    void settled.then(() => {
      // Only clear the run this call registered — never a newer one.
      if (this._pendingDubRun === settled) {
        this._pendingDubRun = null;
        this._cancelPendingDubRun = null;
      }
    });
  }

  /**
   * Wait for the dub run that is out, if there is one, and never for good.
   *
   * Resolves at once when nothing is running, which is every publish that has
   * no dub involved. On expiry it resolves too, rather than leaving the person
   * with a publish that never happens — but it says `expired`, and the caller is
   * expected to refuse the publish on that.
   *
   * Expiry also cancels the run. The bound is per run, not per publish.
   */
  async waitForPendingDubRun(): Promise<PendingDubRunWaitOutcome> {
    const pending = this._pendingDubRun;
    if (!pending) return 'no-run';

    let timer: ReturnType<typeof setTimeout> | undefined;
    const bound = new Promise<'timeout'>(resolve => {
      timer = setTimeout(() => resolve('timeout'), DUB_RUN_WAIT_TIMEOUT_MS);
    });
    const finished: Promise<'settled'> = pending.then(() => 'settled');

    try {
      const outcome = await Promise.race([finished, bound]);
      if (outcome === 'timeout') {
        // Give this run up for good — both halves of that.
        //
        // Stop tracking it: the promise may never settle, and the registration
        // is only cleared when it does, so leaving it here would charge the same
        // wait to every later publish in this session.
        //
        // Stop the run itself. Untracking alone left it alive, so the read
        // carried on and still placed the dub when it returned — after a publish
        // had already gone out without it, leaving the clip and the delivered
        // file disagreeing. Cancelling drops the late result and returns the
        // control to Original, so the person can see the attempt failed and
        // press again.
        //
        // Acted on only if it is still the run THIS call waited on: a fresh
        // press may have registered a newer one, which is genuinely out.
        //
        // The fields are cleared BEFORE `cancel` runs, because cancelling frees
        // the control and a press could register a fresh run inside that call;
        // clearing afterwards would wipe the new run's registration.
        if (this._pendingDubRun === pending) {
          const cancel = this._cancelPendingDubRun;
          this._pendingDubRun = null;
          this._cancelPendingDubRun = null;
          cancel?.();
        }
        logger.warn(
          'A dub run was still running when Publish was pressed and did not finish in time; cancelling it and refusing the publish',
          { timeoutMs: DUB_RUN_WAIT_TIMEOUT_MS },
        );
        return 'expired';
      }
      return 'settled';
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /**
   * Internal background save — delegates to backgroundVideoSaveService,
   * applies results back to session store, and drains the pending queue.
   */
  private async _runBackgroundSave(
    withRenderer: boolean,
    autoSave: boolean,
    queued = false,
  ): Promise<void> {
    try {
      const prepareStart = performance.now();
      const prepared = await backgroundVideoSaveService.prepare(
        this as unknown as IVideoCreatorStore,
        withRenderer,
        autoSave,
      );
      const prepareEnd = performance.now();
      logger.debug('_runBackgroundSave:prepare-complete', {
        durationMs: prepareEnd - prepareStart,
      });

      const saveStart = performance.now();
      const result = await backgroundVideoSaveService.save(
        prepared,
        this as unknown as IVideoCreatorStore,
      );
      const saveEnd = performance.now();
      logger.debug('_runBackgroundSave:save-complete', {
        durationMs: saveEnd - saveStart,
      });

      if (result) {
        // PLAT-11063 — inside the success branch: a save that comes back with
        // nothing is the `background-save-returned-null` failure reported below,
        // and counting it would put a full set of timings against a save that
        // persisted nothing (DS2).
        this._trackSaveCompleted({
          queued,
          prepareMs: prepareEnd - prepareStart,
          saveMs: saveEnd - saveStart,
        });
        this._applyBackgroundSaveResult(result, autoSave);
      } else if (this.currentVideoLoadingWorkflowId) {
        this.rootStore.analyticsStore.completeWorkflow(
          this.currentVideoLoadingWorkflowId,
          false,
          { error: 'background-save-returned-null', autoSave },
        );
        this.currentVideoLoadingWorkflowId = null;
        this.rootStore.videoSourceStore.currentVideoLoadingWorkflowId = null;
      }
    } catch (error) {
      logger.error('_runBackgroundSave:failed', {
        error: extractErrorInfo(error),
      });
      // Wrapped rather than guarded: a rejection that isn't an Error (a
      // GraphQL error object, a string) still failed the save, and dropping it
      // here left the run looking successful.
      this.savingError =
        error instanceof Error ? error : new Error(String(error));

      if (this.currentVideoLoadingWorkflowId) {
        this.rootStore.analyticsStore.completeWorkflow(
          this.currentVideoLoadingWorkflowId,
          false,
          { error: String(error), autoSave },
        );
        this.currentVideoLoadingWorkflowId = null;
        this.rootStore.videoSourceStore.currentVideoLoadingWorkflowId = null;
      }
    } finally {
      this.isBackgroundSaving = false;

      // Notify all callers waiting on this background save cycle
      const resolvers = this._backgroundSaveResolvers;
      this._backgroundSaveResolvers = [];

      if (this.savingError) {
        for (const r of resolvers) {
          r.reject(
            new Error(`Background save failed: ${this.savingError.message}`),
          );
        }
        if (resolvers.length > 0) {
          logger.debug('_runBackgroundSave: rejected waiters due to error', {
            count: resolvers.length,
            error: this.savingError.message,
          });
        }
      } else {
        for (const r of resolvers) {
          r.resolve();
        }
        if (resolvers.length > 0) {
          logger.debug('_runBackgroundSave: resolved waiters', {
            count: resolvers.length,
          });
        }
      }

      // If a save was queued while we were saving, fire it now
      const pending = this._pendingNextSave;
      if (pending) {
        this._pendingNextSave = null;
        this._nextSaveQueued = true;
        this.saveCurrentAndParentVideos(
          pending.withRenderer,
          true,
          pending.autoSave,
        );
      }
    }
  }

  /**
   * PLAT-11063 — a completed save, reported with what it was flushing, what it
   * cost, and (for a reel) the reel's whole shape at that moment.
   *
   * The Reel Editor has no Save button: every edit schedules the debounced
   * auto-save, so this is the only moment a reel is persisted and therefore the
   * only place the census can be taken (DS4). One row per save is more than a
   * census needs, but the last row per clip *is* the final shape — so "did the
   * generated names survive" is a latest-per-reel query rather than a replay of
   * the edit stream, and every earlier row buys the latency series.
   *
   * Fires for every clip-preview save, not only reel ones (DS8): the path is
   * shared, and the non-reel baseline is what makes a reel's number mean
   * anything. A save that throws or returns nothing fires nothing — the caller
   * already logs both, and save reliability is an OPERATIONAL question rather
   * than this one. The reasons such a save was flushing stay pending and roll
   * into the next one, which is where they will actually be persisted.
   */
  private _trackSaveCompleted(timing: {
    queued: boolean;
    prepareMs: number;
    saveMs: number;
  }): void {
    // Drained whether or not this is a reel: reasons left behind would be
    // reported against whichever save happened to run next.
    const triggers = [...this._pendingSaveReasons];
    this._pendingSaveReasons.clear();

    const clipJson = this.currentVideo?.clipJson as ClipMetadata | undefined;
    const isReel = !!clipJson?.isReel;
    const names = Object.values(clipJson?.reel?.sections ?? {});

    EventTracker.trackReelEditorSaved({
      clip_id: this.currentVideo?.id,
      is_reel: isReel,
      triggers,
      trigger_count: triggers.length,
      queued: timing.queued,
      prepare_ms: Math.round(timing.prepareMs),
      save_ms: Math.round(timing.saveMs),
      total_ms: Math.round(timing.prepareMs + timing.saveMs),
      ...(isReel
        ? {
            section_count: deriveReelSections(this._editorStore.editor.timeline)
              .length,
            chapter_count: clipJson?.reel?.chapters.length ?? 0,
            user_named_count: names.filter(n => n.nameSource === 'user').length,
            generated_name_count: names.filter(
              n => n.nameSource === 'generated',
            ).length,
          }
        : {}),
    });
  }

  /**
   * Apply the result of a successful background save back to local state.
   */
  private _applyBackgroundSaveResult(
    result: BackgroundSaveResult,
    autoSave: boolean,
  ) {
    const { id, slug, hash, versionId, wasNewVideo, videoSnapshot } = result;
    const ar = videoSnapshot.aspectRatio;

    (videoSnapshot as any)._publishedAt = new Date().toISOString();

    if (wasNewVideo) {
      this._sessionStore.updateVariant(ar, { id });
    }
    this._sessionStore.updateVariant(ar, {
      slug,
      hash,
      // PLAT-11911: the version this content now is, for a Creator Studio seed.
      ...(versionId && { versionId }),
      resourcesMetadata: videoSnapshot.resourcesMetadata,
      videoSource: videoSnapshot.videoSource,
      transcriptionSnapshot: videoSnapshot.transcriptionSnapshot,
      karaokeData: videoSnapshot.karaokeData,
      karaokeDataByLanguage: videoSnapshot.karaokeDataByLanguage,
      scenes: videoSnapshot.scenes,
    });

    // Update story.otherVideos with saved state so re-opening the modal
    // picks up the latest videoSource (incl. template elements) and
    // extraElementData instead of stale data from the initial expansion.
    if (this._realVideoCreator?.story?.otherVideos) {
      syncVideoToStoryEntries(
        this._realVideoCreator.story.otherVideos,
        id,
        // prepareCurrentVideoForSave was called on videoSnapshot,
        // currentVideo may have stale transcriptionSnapshot
        videoSnapshot,
      );
    }

    // Invalidate the hover-prefetch cache so the next open of this clip
    // fetches fresh data instead of the stale pre-edit snapshot. Pass the
    // parent id too: the cache is keyed by the top-level (mainClip) id that
    // VideoClipCard uses for prefetch/enrich, and invalidating `id` alone
    // misses when `id` is a non-main variant.
    this._realVideoCreator.rootStore.clipPreviewStore.invalidate(
      id,
      this._sessionStore.mainClip.id,
    );
    // PLAT-11911: Creator Studio still holding this clip now holds an older
    // copy; its next open (sidebar, "Edit Video") must not reuse it.
    if (this._realVideoCreator.currentVideo?.id === id) {
      this._realVideoCreator.releaseLoadedVideo();
    }

    // Complete video loading workflow on first save
    if (this.currentVideoLoadingWorkflowId) {
      this.rootStore.analyticsStore.completeWorkflow(
        this.currentVideoLoadingWorkflowId,
        true,
        { videoId: id, autoSave },
      );
      this.currentVideoLoadingWorkflowId = null;
      this.rootStore.videoSourceStore.currentVideoLoadingWorkflowId = null;
    }

    this.lastSavedAt = new Date();
  }

  /**
   * Prepare current video for save.
   *
   * When a `snapshot` is provided (e.g. undo/redo path), the fields are
   * mutated directly on that local copy.  When omitted the method collects
   * all fields and writes them through `sessionStore.updateVariant` so
   * that the `observable.ref` contract is honoured.
   */
  prepareCurrentVideoForSave = async (
    withRenderer = true,
    snapshot?: CurrentLoadedVideo,
  ) => {
    const isLive = !snapshot;
    const videoToModify =
      snapshot ?? (this.currentVideo as unknown as CurrentLoadedVideo);
    if (!videoToModify) return;

    // Accumulates fields that must be flushed to the session store when
    // operating on the live variant (no snapshot supplied).
    const pendingFields: Record<string, unknown> = {};

    if (withRenderer) {
      const videoSource = this._editorStore.editor.timeline.toStoredJSON();
      if (!videoSource.elements) {
        throw { humanMessage: 'Video is not yet loaded' };
      }

      // An editor with nothing on it must not blank a recipe that has
      // something. The check above cannot catch that: `toStoredJSON` always
      // returns an elements array and an EMPTY array is truthy, so an editor
      // that has not loaded yet sails past it and its nothing is written over
      // the real recipe. Everything harvested below comes from the same empty
      // editor, so the whole flush stops rather than only the recipe write.
      const storedElementCount: number | undefined =
        videoToModify.videoSource?.elements?.length;
      if (videoSource.elements.length === 0 && (storedElementCount ?? 0) > 0) {
        logger.warn(
          'Skipping the save: this clip editor has nothing on its timeline, and the clip already has a recipe',
          { storedElementCount },
        );
        return;
      }

      const restoredVideoSource = videoSource;

      if (isLive) {
        pendingFields.videoSource = restoredVideoSource;
      } else {
        videoToModify.videoSource = restoredVideoSource;
      }

      // Always persist duration into clipJson for reliable display in listing views
      const d = this.duration;
      const existingClipJson = videoToModify.clipJson;
      // PLAT-11052 (DE13) — a reel's subClips must be re-derived from the
      // timeline on every save. They are what the lightweight-expansion path
      // re-crops from, so leaving them as the producer wrote them means a
      // reordered reel disagrees with itself about its own order: it looks
      // right until it is expanded, then plays in the original order.
      // `isReel` rides along in the spread, so an edited reel stays a reel.
      // The stored entries are passed in so each Section's recorded lineage
      // (PLAT-11067) survives the re-derive — the timeline knows a Section's
      // media range but not which clip contributed it. The names go in for the
      // same reason and take precedence over the titles those entries carry:
      // `subClips[].title` is what names a Section when this reel is combined
      // into another (DE8), so it has to be the name the reel goes by now, not
      // the seed the producer wrote before the reel had Sections at all.
      const rederivedSubClips = existingClipJson?.isReel
        ? deriveSubClipsFromTimeline(
            this._editorStore.editor.timeline,
            existingClipJson.subClips,
            existingClipJson.reel?.sections,
          )
        : undefined;

      // PLAT-11052 (DE13) — and the other direction: the reel's stored names
      // only ever grew, because membership is read off the timeline rather than
      // repaired on write. The save is where names for Sections that have since
      // left the reel are dropped, so the map stays one entry per live Section
      // instead of one per Section the clip has ever held. Chapters are left
      // alone on purpose — see `pruneReelSectionNames`.
      const storedReel = existingClipJson?.reel;
      const prunedReel = Object.keys(storedReel?.sections ?? {}).length
        ? pruneReelSectionNames(
            storedReel,
            deriveReelSections(this._editorStore.editor.timeline).map(
              section => section.id,
            ),
          )
        : storedReel;

      if (
        (typeof d === 'number' && d > 0 && isFinite(d)) ||
        rederivedSubClips?.length ||
        prunedReel !== storedReel
      ) {
        const updatedClipJson: ClipMetadata = {
          ...((existingClipJson ?? { startTime: 0 }) as ClipMetadata),
        };
        if (prunedReel !== storedReel) {
          updatedClipJson.reel = prunedReel;
        }
        if (typeof d === 'number' && d > 0 && isFinite(d)) {
          updatedClipJson.duration = d;
        }
        if (rederivedSubClips?.length) {
          updatedClipJson.subClips = rederivedSubClips;
          // The card thumbnail is a frame taken at `startTime`, so a reorder
          // has to carry it to whichever Section now opens the reel.
          updatedClipJson.startTime = rederivedSubClips[0].startTime;
        }
        if (isLive) {
          pendingFields.clipJson = updatedClipJson;
        } else {
          videoToModify.clipJson = updatedClipJson;
        }
      }

      if (videoToModify.clipJson) {
        // Always sync the full clipJson from the authoritative source (videoToModify)
        // to story.otherVideos. This ensures fields like isLightweightClip: false
        // (set during expansion) are propagated and not lost due to stale spreads.
        const clipJsonForSync =
          (pendingFields.clipJson as typeof videoToModify.clipJson) ??
          videoToModify.clipJson;
        for (const storyVideo of this.story?.otherVideos || []) {
          if (storyVideo.clipJson && storyVideo.id === videoToModify.id) {
            storyVideo.clipJson = { ...clipJsonForSync };
          }
        }
      }
    }
    if (this.story?.byExternalUser) {
      if (isLive) {
        pendingFields.isClientReady = true;
      } else {
        videoToModify.isClientReady = true;
      }
    }

    // PLAT-10788 (DS5) — harvest karaoke from the clip's OWN per-instance
    // KaraokeStore (not the global stores). Harvest-only: `activeLanguage` is
    // owned by clip load/switch (Phase 4/5), so the per-language map is empty
    // until then — matching the main path.
    const clipKaraokeStore = this._editorStore.karaokeStore;
    const clipKaraokeData = clipKaraokeStore.getKaraokeData() ?? undefined;
    const clipKaraokeByLanguageMap =
      clipKaraokeStore.getAllKaraokeDataByLanguage();
    const clipKaraokeByLanguage =
      Object.keys(clipKaraokeByLanguageMap).length > 0
        ? clipKaraokeByLanguageMap
        : undefined;

    // Get karaoke config from the clip's editor (for extraElementData).
    const clipKaraokeConfig =
      this._editorStore.editor.getDefaultKaraokeConfig();
    let karaokeConfig = clipKaraokeConfig || null;

    // Get transcript from the CLIP'S editor, not the global TranscriptionStore.
    // The clip editor holds the authoritative transcript state after user edits.
    // Cast needed: getFullTranscript() returns ActiveTranscriptToken[] with ts: number|null
    // (known type/runtime mismatch for removed elements), but save expects TranscriptElement[].
    const transcriptSnapshot =
      this._editorStore.editor.getFullTranscript() as any[];

    // Capture layout segments from timeline
    const scenes = this._editorStore.editor.timeline.scenes.map(s =>
      s.toJSON(),
    );

    // Build extraElementData with karaoke config merged in
    let extraElementData = { ...(videoToModify.extraElementData || {}) };
    if (karaokeConfig) {
      extraElementData.karaokeConfig = karaokeConfig;
    }
    // Apply punch list sync
    for (const element of Object.values(extraElementData)) {
      delete (element as any)?.punchListData;
    }
    const punchList = this.punchListManager.punchListItems;
    punchList?.forEach((punchListItem: any) => {
      if (!punchListItem.id) return;
      extraElementData[punchListItem.id] = {
        ...(extraElementData[punchListItem.id] || {}),
        punchListData: punchListItem,
      };
    });

    if (isLive) {
      pendingFields.transcriptionChanges = [];
      pendingFields.karaokeData = clipKaraokeData ?? undefined;
      pendingFields.karaokeDataByLanguage = clipKaraokeByLanguage;
      pendingFields.transcriptionSnapshot = { elements: transcriptSnapshot };
      pendingFields.transcriptionText =
        extractTranscriptionText(transcriptSnapshot);
      pendingFields.scenes = scenes;
      pendingFields.extraElementData = extraElementData;
      pendingFields.punchList = punchList;

      this._sessionStore.updateVariant(
        this.getCurrentAspectRatio(),
        pendingFields,
      );
    } else {
      videoToModify.transcriptionChanges = [];
      videoToModify.karaokeData = clipKaraokeData ?? undefined;
      videoToModify.karaokeDataByLanguage = clipKaraokeByLanguage;
      videoToModify.transcriptionSnapshot = { elements: transcriptSnapshot };
      videoToModify.transcriptionText =
        extractTranscriptionText(transcriptSnapshot);
      videoToModify.scenes = scenes;
      videoToModify.extraElementData = extraElementData;
      this.syncPunchListToVideo(videoToModify);
    }
  };

  /**
   * Sync punch list to video
   */
  private syncPunchListToVideo(videoToModify: Video): void {
    const extraElementData = videoToModify.extraElementData || {};
    for (let element of Object.values(extraElementData)) {
      delete element?.punchListData;
    }
    videoToModify.punchList = this.punchListManager.punchListItems;
    videoToModify.punchList?.forEach(punchListItem => {
      if (!punchListItem.id) return;
      extraElementData[punchListItem.id] = {
        ...(extraElementData[punchListItem.id] || {}),
        punchListData: punchListItem,
      };
    });
    videoToModify.extraElementData = extraElementData;
  }

  /**
   * Add current video to parent videos
   */
  async addCurrentToParentVideos(
    videoSnapshot?: CurrentLoadedVideo,
    storySnapshot?: Story,
  ) {
    const videoToModify =
      videoSnapshot ?? (this.currentVideo as unknown as CurrentLoadedVideo);
    const storyToModify = storySnapshot ?? this.story;

    if (!videoToModify) {
      logger.warn('addCurrentToParentVideos: no video to modify');
      return;
    }
    if (!videoToModify.parentVideo) {
      logger.warn('addCurrentToParentVideos: no parent video found');
      return;
    }

    // Strip circular back-references so the entry is safe to push into
    // MobX-observable arrays (which deep-observe on insertion).
    // Without this, videoToModify.parentVideo.associatedVideos → videoToModify
    // → parentVideo → … creates an infinite loop during deep observation.
    const flatVideo = {
      ...videoToModify,
      parentVideo: undefined,
      associatedVideos: [],
    };

    // Read fresh associatedVideos from the session store instead of the
    // stale deep-cloned parentVideo.associatedVideos. During rapid aspect
    // ratio switching, the parentVideo snapshot captured at variant creation
    // time may contain sibling variants without ids (their saves hadn't
    // completed yet). The session store's _variants map is the single source
    // of truth and always has up-to-date ids.
    const freshMainClip = this._sessionStore.mainClip;
    const freshSiblings = (freshMainClip.associatedVideos ?? []).filter(
      (v: AssociatedVideo) => v.id && v.id !== videoToModify.id,
    );
    const updatedAssociatedVideos = [...freshSiblings, flatVideo];

    const parentVideo = videoToModify.parentVideo;
    const videoData =
      await this.datoClientStore.videoRepository.saveOrUpdateVideo(
        parentVideo.id
          ? {
              id: parentVideo.id,
              associatedVideos: updatedAssociatedVideos,
            }
          : { ...parentVideo, associatedVideos: updatedAssociatedVideos },
      );

    videoToModify.parentVideo.id = videoData.id;
    const parentStoryVideo = storyToModify?.otherVideos.find(
      v => v.id === videoToModify.parentVideo?.id,
    );
    if (parentStoryVideo) {
      const alreadyInStoryParent = parentStoryVideo.associatedVideos.some(
        v => v.id === videoToModify.id,
      );
      if (!alreadyInStoryParent) {
        parentStoryVideo.associatedVideos.push(flatVideo);
      }
    }
  }

  /**
   * Add current video to story videos
   */
  async addCurrentToStoryVideos(
    videoSnapshot?: CurrentLoadedVideo,
    storySnapshot?: Story,
  ) {
    const videoToRead =
      videoSnapshot ?? (this.currentVideo as unknown as CurrentLoadedVideo);
    const storyToModify = storySnapshot ?? this.story;

    if (!videoToRead?.id) {
      throw new Error('Current video id is not set');
    }
    if (!storyToModify?.id) {
      throw new Error('Story video id is not set');
    }

    await this.datoClientStore.storyRepository.addVideoToStory(
      storyToModify.id,
      videoToRead.id,
    );
    storyToModify.otherVideos = [
      videoToRead as any,
      ...storyToModify.otherVideos,
    ];
  }

  /**
   * Add video action to current video
   */
  public addVideoAction(
    newVideoAction: VideoAction,
    videoSnapshot?: CurrentLoadedVideo,
    storySnapshot?: Story,
  ): void {
    const videoToModify =
      videoSnapshot ?? (this.currentVideo as unknown as CurrentLoadedVideo);
    const storyToModify = storySnapshot ?? this.story;
    if (!videoToModify) {
      return;
    }
    if (videoSnapshot) {
      // Snapshot path: mutate the local copy directly
      videoToModify.lastActionJson = newVideoAction;
    } else {
      // Live path: write through the session store
      this._sessionStore.updateVariant(this.getCurrentAspectRatio(), {
        lastActionJson: newVideoAction,
      });
    }
    for (const storyVideo of storyToModify?.otherVideos || []) {
      if (storyVideo.id === videoToModify.id) {
        storyVideo.lastActionJson = newVideoAction;
      }
    }
  }

  /**
   * No-op: version history is not displayed in clip preview context.
   * Retained as stubs to satisfy IVideoCreatorStore interface and
   * TextBrandingService call path.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  async loadCurrentVideoVersionsHistory(_page: number = 0) {
    // No-op
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  async loadVersionsHistory(_videoId: string, _page: number = 0) {
    // No-op
  }

  /**
   * Update story
   */
  async updateStory(
    story: Partial<StoryDTO> & { id: string },
  ): Promise<DatoStory> {
    return this.datoClientStore.storyRepository.update(story);
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get the editor store for direct access
   */
  getEditorStore(): ClipPreviewEditorStore {
    return this._editorStore;
  }

  /**
   * Get the video source store for direct access
   */
  getVideoSourceStore(): VideoSourceStore {
    return this._videoSourceStore;
  }

  /**
   * Get the extra element data (for saving changes)
   */
  getExtraElementData(): Record<string, unknown> {
    return this.currentVideo.extraElementData;
  }

  /**
   * Update the renderer reference (called when renderer changes)
   */
  updateRenderer(renderer: Renderer | null): void {
    this._renderer = renderer;
  }

  saveVideoStateSnapshot(into: 'undo' | 'redo' = 'undo', label?: string): void {
    if (!this.currentVideo) return;

    const snapshot = this.buildSnapshot(label);

    if (into === 'undo') {
      this.undoSnapshots.push(snapshot);
      if (this.undoSnapshots.length > MAX_UNDO_REDO_STACK_SIZE) {
        this.undoSnapshots.shift();
      }
      this.redoSnapshots = [];
    } else {
      this.redoSnapshots.push(snapshot);
      if (this.redoSnapshots.length > MAX_UNDO_REDO_STACK_SIZE) {
        this.redoSnapshots.shift();
      }
    }
  }

  /**
   * Deep-clone a video object while avoiding the OOM caused by circular
   * parentVideo ↔ associatedVideos references.
   *
   * toJS() unwraps MobX proxies into a plain object tree. The resulting
   * copy still contains circular back-links
   * (parentVideo.associatedVideos[n].parentVideo → …) that structuredClone
   * cannot handle, so we null out the back-references before cloning.
   *
   * The two spreads are load-bearing: `toJS` returns a NON-observable input
   * unchanged (clip variants are plain — `_variants` is `observable.ref`), so
   * severing the back-links in place would edit the CALLER's object. On the
   * `buildSnapshot` path that object is the live variant, which would lose its
   * `parentVideo` back-links on every command.
   */
  private cloneVideoWithoutCircularRefs(
    video: CurrentLoadedVideo,
  ): CurrentLoadedVideo {
    const copy: CurrentLoadedVideo = { ...toJS(video) };
    if (copy.parentVideo) {
      copy.parentVideo = {
        ...copy.parentVideo,
        associatedVideos: copy.parentVideo.associatedVideos.map(v => ({
          ...v,
          parentVideo: undefined,
        })),
      };
    }
    return deepCloneSafe(copy) as CurrentLoadedVideo;
  }

  private buildSnapshot(
    label?: string,
  ): CurrentLoadedVideo & { label?: string } {
    const videoSourceClone =
      this._editorStore.exportProject().sources.videoSource;
    // PLAT-11150 — deliberately NOT cloned. Both of these are already freshly
    // built, fully-owned plain data, so cloning them allocated a second copy of
    // the whole transcript and every scene on every command plus GCs after it
    //
    //  - `getFullTranscript()` returns a new array of `{...token}` spread
    //    copies, and every property in the token shape is a primitive
    //    (`src/editor/types.ts`), so the spread is already a complete copy.
    //  - `Scene.toJSON()` deep-clones its anchors and delegates to
    //    `layout.toJSON()`, so it hands back fresh data too.
    //
    // What makes this safe is that the editor allocates rather than mutating in
    // place, so a snapshot cannot observe a later edit — pinned by
    // `ClipVideoCreatorAdapter.snapshotIsolation.test.ts`. If a nested object
    // is ever added to the token shape that invariant breaks
    const transcriptClone =
      this._editorStore.editor.getFullTranscript() as TranscriptElement[];
    const scenesClone = this._editorStore.editor.timeline.scenes.map(scene =>
      scene.toJSON(),
    );
    // PLAT-11150 — the clip carries its own copy of all four of the fields
    // rebuilt above, and `transcriptionSnapshot` is the whole transcript again.
    // `cloneVideoWithoutCircularRefs` would deep-clone every one of them, and
    // the spread below then throws all four away — so dropping them first is
    // what actually removes the transcript-sized allocation this commit is
    // about. Everything the snapshot keeps is still cloned.
    const {
      videoSource: _replacedVideoSource,
      transcriptionSnapshot: _replacedTranscriptionSnapshot,
      karaokeData: _replacedKaraokeData,
      scenes: _replacedScenes,
      ...currentVideoWithoutRebuiltFields
    } = this.currentVideo as unknown as CurrentLoadedVideo;
    const currentVideoClone = this.cloneVideoWithoutCircularRefs(
      currentVideoWithoutRebuiltFields as CurrentLoadedVideo,
    );

    // Eagerly capture karaoke config from the editor so undo/redo doesn't
    // restore a stale value that was last written to the session store.
    const karaokeConfig =
      this._editorStore.editor.getDefaultKaraokeConfig() ?? undefined;
    if (karaokeConfig && currentVideoClone.extraElementData) {
      currentVideoClone.extraElementData.karaokeConfig = karaokeConfig;
    }

    return {
      ...currentVideoClone,
      videoSource: videoSourceClone,
      transcriptionSnapshot: { elements: transcriptClone },
      karaokeData:
        getKaraokeDataFromEditor(this._editorStore.editor) ?? undefined,
      scenes: scenesClone,
      // PLAT-10816 — must come after the clone above, which carries a possibly
      // stale `karaokeDataByLanguage` from the session variant.
      ...captureKaraokeLanguageState(this._editorStore.karaokeStore),
      label,
    };
  }

  private applySnapshot(snapshot: VideoStateSnapshot): void {
    // PLAT-11150 — applied as-is, not cloned.
    //
    // `undo`/`redo` POP the snapshot before handing it here, and the entry they
    // push in its place is a fresh `buildSnapshot` of the CURRENT state — so
    // this object has no other owner, and it goes on to become the live variant
    // via `replaceVariantData` below, which is precisely what a clone would
    // have become. The clone only ever produced a second object for the one the
    // stack had already given up.
    //
    // The two things the editor is handed that it could mutate are copied by
    // their own consumers regardless: `prepareVideoSourceForEditor` opens with
    // a `deepCloneSafe`, and the scenes are cloned per scene below.
    // `switchToExistingVariant` already loads the LIVE variant's transcript,
    // videoSource and karaoke through the same `loadProjectForPreview`.

    // PLAT-11054 — a snapshot carries the whole clip, `clipJson` included, so
    // restoring one would also restore the reel's names as they were when it
    // was taken. Names are not timeline state (DE13): undoing a trim must not
    // silently drop a Section renamed since. Carry the live `reel` across; the
    // rest of `clipJson` — `subClips` above all, which *is* timeline-derived —
    // is restored with the snapshot.
    //
    // PLAT-11075 DS8 narrows that to *titles*: a chapter boundary is positional
    // and is written by the same drag that moved the timeline, so an undo that
    // left it behind would put the Section back in the wrong chapter. Snapshots
    // are taken before each command, so this one already holds the anchors as
    // they were.
    const liveClipJson = this.currentVideo?.clipJson as
      | ClipMetadata
      | undefined;
    const restoredClipJson = snapshot.clipJson as ClipMetadata | undefined;
    if (restoredClipJson) {
      restoredClipJson.reel = restoreChapterAnchors(
        liveClipJson?.reel,
        restoredClipJson.reel,
      );
    }

    this._sessionStore.replaceVariantData(
      this.getCurrentAspectRatio(),
      snapshot,
    );

    if (!snapshot.videoSource) return;

    const preparedVideoSource = this.prepareVideoSourceForEditor(
      snapshot.videoSource,
      this.getCurrentAspectRatio(),
    );
    const scenesClone = (snapshot.scenes ?? []).map(scene =>
      deepCloneSafe(scene),
    );

    const originalVideoTrackId =
      this._realVideoCreator.pickOriginalVideoTrack(preparedVideoSource) ??
      null;

    if (originalVideoTrackId == null) {
      logger.warn(
        'applySnapshot: video track id not found; falling back to null',
        {
          clipId: this.currentVideo?.id,
          elementCount: preparedVideoSource?.elements?.length ?? 0,
        },
      );
    }

    // PLAT-10816 — the load below hydrates karaoke by preferring
    // `activeLanguage`, so point it at the language this snapshot was taken in
    // or the restore falls back to the source variant.
    const seededLanguage = seedKaraokeLanguageFromSnapshot(
      this._editorStore.karaokeStore,
      snapshot,
    );

    this._editorStore.loadProjectForPreview({
      videoSource: preparedVideoSource,
      transcript: (snapshot.transcriptionSnapshot?.elements ||
        []) as TranscriptElement[],
      originalTranscription:
        this._realVideoCreator.rootStore.transcriptionStore.getOriginalTranscription()
          ?.elements as TranscriptElement[] | undefined,
      karaokeData: snapshot.karaokeData ?? undefined,
      forcedAlignment:
        this._realVideoCreator.rootStore.transcriptionStore.isForcedAligned,
      // PLAT-10788 — per-language karaoke + language context (DS8/DS9).
      karaokeDataByLanguage: snapshot.karaokeDataByLanguage,
      transcriptLanguage:
        this._realVideoCreator.rootStore.transcriptionStore.rawLanguage,
      originalVideoLanguage: this.story?.originalVideoLanguage,
      scenes: scenesClone,
      originalVideoTrackId,
      extraElementData: this.currentVideo.extraElementData,
    });

    // PLAT-10816 — mirror what the load actually landed on, not what the
    // snapshot asked for: hydration falls back to the source variant when the
    // requested language has no entry, and the picker must agree with the
    // captions on screen.
    if (seededLanguage) {
      this._sessionStore.setSelectedLanguage(
        resolveRestoredUiLanguage(this._editorStore.karaokeStore),
      );
    }

    // Clear the editor's internal command history so stale commands from the
    // pre-undo state can't be replayed against the restored state.
    try {
      this._editorStore.editor?.clearHistory();
    } catch (err) {
      logger.error('applySnapshot: clearing editor history failed', {
        error: extractErrorInfo(err),
      });
    }
  }

  // ============================================================================
  // PUBLISHING
  // ============================================================================

  async publishVideo(): Promise<void> {
    // PLAT-10788 — clip publishing is not routed through the adapter. All clip
    // publishes go through PublishFanoutService (SocialClipShareModal → ExportMenu
    // → usePublishFanout), which handles per-language records, mirrorsToLegacy, and
    // fingerprints correctly. This hand-rolled path hardcoded the original
    // language / mirrorsToLegacy=true and is superseded; throw loudly so it can
    // never be wired back in silently with those bugs.
    throw new Error(
      'ClipVideoCreatorAdapter.publishVideo is not supported — clip publishing goes through PublishFanoutService (ExportMenu → usePublishFanout).',
    );
  }

  // ============================================================================
  // PUBLISHING HELPERS
  // ============================================================================

  getValidVideoSourceUrlForResLevel(resLevel: any): string {
    return this._realVideoCreator.getValidVideoSourceUrlForResLevel(resLevel);
  }

  replaceWithValidVideoSourceUrl(
    source: VideoSource,
    resLevel: VideoResolution,
  ): VideoSource {
    return this._realVideoCreator.replaceWithValidVideoSourceUrl(
      source,
      resLevel,
    );
  }

  setStandardDimensions(
    videoSource: VideoSource,
    aspectRatio: AspectRatio,
  ): void {
    this._realVideoCreator.setStandardDimensions(videoSource, aspectRatio);
  }

  get customFontsService(): CustomFontsService | null {
    return this._realVideoCreator.customFontsService;
  }

  isOriginalVideoElement(elementSource: any): boolean {
    return this._realVideoCreator.isOriginalVideoElement(elementSource);
  }

  /**
   * Check if an element is an image-wrapping composition.
   * Local implementation (not delegated) because it reads this.currentVideo
   * which differs between the adapter (clip variant) and the real store.
   * Mirrors VideoCreatorStore.isImageElementComposition.
   */
  isImageElementComposition(
    element: ElementState | Record<string, any>,
  ): boolean {
    const sourceObj: any = (element as any).source;
    const isCompositionType =
      (typeof sourceObj === 'string' &&
        (element as any).type === 'composition') ||
      sourceObj?.type === 'composition';
    if (!isCompositionType) return false;

    const meta = getElementMetaObject(
      this.currentVideo,
      sourceObj?.id || (element as any).id,
    );
    if (meta && meta.type === 'composition') {
      return false;
    }
    return Array.isArray((element as any).elements)
      ? (element as any).elements.some((e: any) => e.source?.type === 'image')
      : false;
  }

  /**
   * Check if an element is an image (standalone or composition-wrapped).
   * Mirrors VideoCreatorStore.isImageElement.
   */
  isImageElement(element: ElementState): boolean {
    return (
      this.isImageElementComposition(element) ||
      element.source?.type === 'image'
    );
  }

  /**
   * Check if an element is a logo element.
   * Local implementation because it reads this.currentVideo.extraElementData.
   * Mirrors VideoCreatorStore.isLogoElement.
   */
  isLogoElement(element: ElementState): boolean {
    return (
      this.isImageElement(element) &&
      !!(
        this.currentVideo?.extraElementData[
          `logo_el_${element.source.id}`
        ] as ExtraElementData
      )?.isLogo
    );
  }

  /**
   * Find a free media track that doesn't overlap with existing elements
   * of the same type within the given time span.
   * Mirrors VideoCreatorStore.getFreeMediaTrack.
   */
  getFreeMediaTrack(
    type: 'audio' | 'video' | 'image',
    mediaDuration: number,
    currTime = this._time,
  ): number | undefined | null {
    const elementsOfSameType =
      this.getEditorStore().editor.timeline.allElements.filter(el => {
        if (type !== 'image')
          return el.type === type && !this.isOriginalVideoElement(el);
        return (
          (Element.isImageElement(el) || Element.isCompositionElement(el)) &&
          !Element.isLogoElement(el)
        );
      });

    const mediaSpan = currTime + mediaDuration;

    const overlappedTracks =
      elementsOfSameType
        ?.filter(el => {
          const { time, end_time } = el;
          if (
            (time <= currTime && end_time >= currTime) ||
            (time <= mediaSpan && end_time >= mediaSpan)
          ) {
            return true;
          }
          return false;
        })
        ?.map(t => t.track) || [];

    return elementsOfSameType?.find(t => !overlappedTracks.includes(t.track))
      ?.track;
  }

  get stockMusic() {
    return this._realVideoCreator.stockMusic;
  }

  get isVideoCreatorReady(): boolean {
    return this._isInitialized && this._renderer !== null;
  }

  // ============================================================================
  // STUB IMPLEMENTATIONS (Required by interface but not needed for clip preview)
  // ============================================================================

  // These properties/methods are required by IVideoCreatorStore interface
  // but are either not used or have minimal implementations for clip preview

  isLoading: boolean = false;
  isError: boolean = false;
  // When the Translation Review workspace owns playback via its compact
  // player, this stays true so transcript seek handlers don't also resume
  // THIS clip renderer (a second audio source). Plain field, excluded from
  // observability below — it's read synchronously in seek callbacks, not in
  // render. (PLAT-10286)
  suppressRendererPlayback = false;
  get isSaving(): boolean {
    return this.isBackgroundSaving;
  }
  set isSaving(value: boolean) {
    this.isBackgroundSaving = value;
  }
  isBackgroundSaving: boolean = false;
  isPlaying: boolean = false;

  // Pre-loaded detection data — delegated from real VideoCreatorStore.
  // The adapter does not pre-load these itself; reads go through
  // _realVideoCreator in performCreateScenesForNewAspectRatio.
  preloadedNaturalDimensions: Record<
    string,
    { width: number; height: number }
  > = {};
  activeElementIds: string[] = [];
  selectedTrack: number = 0;
  tracks?: Map<number, ElementState[]>;
  thumbnailLoadingElements: Set<string> = new Set();
  smootherTime: number = 0;
  timelineScale: number = 100;
  showRepublishOnSaveModal: boolean = false;
  defaultTimelineScale: number = 100;
  maxTimelineScale: number = 400;
  isPlayheadDragging: boolean = false;
  isScrubbing: boolean = false;

  /**
   * Monotonic seek counter, so a seek that waited on a window rebuild can
   * tell it has been superseded and stand down (PLAT-11119).
   */
  private _seekSeq = 0;
  isSnapGuideVisible: boolean = false;
  snapGuideTime: number = 0;
  isPlayheadSnapHighlighted: boolean = false;
  timelineClipboard: null = null;
  frameLockedTracks: number[] = [];
  tempDragTime: number | null = null;
  isPlayerFullScreen: boolean = false;
  karaokeLoading: boolean = false;
  stateReady: boolean = true;
  isCurrentVideoTheLatest: boolean = true;
  lastClipDimension: Map<string, string> = new Map();

  setShowRepublishOnSaveModal(): void {
    // No-op
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get trackManager(): any {
    return this._editorStore.editor?.getTrackManager?.() ?? null;
  }

  get sceneLayoutSelectionManager(): SceneLayoutSelectionManager {
    return this._sceneLayoutSelectionManager;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get statusUpdateManager(): any {
    return null;
  }

  // ============================================================================
  // TRANSCRIPT SUPPORT (Phase 1: read-only transcript in clip preview modal)
  // ============================================================================

  /**
   * Update the current playback time from renderer callbacks.
   * Called by useClipPreviewRenderer when the renderer reports time changes.
   */
  updateTime(time: number): void {
    runInAction(() => {
      this._time = time;
    });
  }

  /**
   * Update the play/pause state from renderer callbacks.
   * Called by useClipPreviewRenderer when the renderer reports play/pause.
   */
  updatePlayState(playing: boolean): void {
    runInAction(() => {
      this.isPlaying = playing;
    });
  }

  getShowSubtitles(): boolean {
    return this.showSubtitles;
  }

  async refreshSourceAndPlay(): Promise<void> {
    // PLAT-11119 — playback runs forward from the playhead, so the window has
    // to reach it before the renderer starts rather than 500ms into the run.
    await this._editorStore.ensureWindowCovers(this._time);
    await this._renderer?.play();
  }

  getSourceVideoFrameRate(): number {
    return this.timelineStore.framerate;
  }

  async getNaturalMediaDimensions(): Promise<
    Record<string, { width: number; height: number }>
  > {
    return this._realVideoCreator.getNaturalMediaDimensions();
  }

  recalculateTimelineScaleIfAtDefaultZoom(): void {
    // No-op for clip preview — no visible timeline to rescale
  }

  recalculateTimelineScale(): void {
    // No-op for clip preview — no visible timeline to rescale
  }

  addShareableImage(shareableImage: ShareableImageType): void {
    this._realVideoCreator.addShareableImage(shareableImage);
  }

  prepareVideoSourceForEditor(
    videoSourceInput: VideoSource,
    aspectRatio: AspectRatio,
    resolution: VideoResolution = VideoResolution.Low,
  ): VideoSource {
    let videoSourceOutput: VideoSource = deepCloneSafe(videoSourceInput);
    this.setStandardDimensions(videoSourceOutput, aspectRatio);
    videoSourceOutput = this.replaceWithValidVideoSourceUrl(
      videoSourceOutput,
      resolution,
    );
    videoSourceOutput = hydrateOriginalMediaDuration(
      videoSourceOutput,
      this as unknown as IVideoCreatorStore,
    );
    if (this.customFontsService) {
      videoSourceOutput =
        this.customFontsService.getCustomFontsSource(videoSourceOutput);
    }
    return videoSourceOutput;
  }

  // Index signature to satisfy IVideoCreatorStore interface
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-undef
  [key: string]: any;
}
